import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonButton,
  IonIcon,
  IonSpinner,
  IonToast,
  IonAlert,
  IonPopover,
  IonModal,
} from '@ionic/react';
import {
  addOutline,
  personAddOutline,
  ticketOutline,
  trashOutline,
  refreshOutline,
  checkmarkDoneOutline,
  funnel,
  funnelOutline,
  expandOutline,
  contractOutline,
  arrowUpOutline,
  arrowDownOutline,
  swapVerticalOutline,
  copyOutline,
  clipboardOutline,
  warningOutline,
  banOutline,
  checkmarkCircleOutline,
  paperPlaneOutline,
  calendarOutline,
  chatbubbleOutline,
} from 'ionicons/icons';
import { io, Socket } from 'socket.io-client';
import { orderService } from '../services/api';
import { getOrderWaStatuses, getOrderWaStatusesFor, markOrderDelivered } from '../services/whatsappService';
import { auth } from '../config/firebase';
import { Order } from '../types';
import { useIsMobile } from '../hooks/useIsMobile';

// The realtime server is the API host without the trailing /api.
const SOCKET_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001/api').replace(/\/api\/?$/, '');

type FieldType = 'text' | 'number' | 'date';

interface ColumnDef {
  key: keyof Order;
  label: string;
  type: FieldType;
  width: string;
  align?: 'left' | 'right';
  // Not rendered in the desktop table (stays in the phone edit sheet + all downstream logic).
  hidden?: boolean;
}

// Column order matches the requested spreadsheet layout. `profit` is computed
// server-side and rendered read-only.
const COLUMNS: ColumnDef[] = [
  { key: 'orderId', label: 'Order Id', type: 'text', width: '130px' },
  { key: 'eventName', label: 'Event Name', type: 'text', width: '200px' },
  { key: 'orderDate', label: 'Order Date', type: 'date', width: '140px' },
  { key: 'eventDate', label: 'Event Date', type: 'date', width: '140px' },
  { key: 'quantity', label: 'Quantity', type: 'number', width: '90px', align: 'right' },
  { key: 'remarks', label: 'Remarks', type: 'text', width: '160px' },
  { key: 'category', label: 'Category', type: 'text', width: '120px' },
  // The raw amount as the platform recorded it — NOT GBP for Hello/StubHub/Vivid etc. The converted
  // value lives in the separate "Total GBP Price" column (totalPriceGBP x the rate locked at import).
  { key: 'totalPriceGBP', label: 'Local Currency Price', type: 'number', width: '130px', align: 'right' },
  { key: 'currency', label: 'Currency', type: 'text', width: '90px' },
  { key: 'platform', label: 'Platform', type: 'text', width: '130px' },
  { key: 'boughtFrom', label: 'Bought From', type: 'text', width: '150px' },
  // Blank on a normal order — only filled in when a sale is PART-bought (see sourcingStatus).
  // Hidden from the table (it's blank on ~every row and was eating width); editable in the phone
  // sheet, and everything it powers — amber rows, Needed/Partial filters, wanted lists — still runs.
  { key: 'ticketsBought', label: 'Tickets Bought', type: 'number', width: '110px', align: 'right', hidden: true },
  { key: 'priceBought', label: 'Price Bought', type: 'number', width: '120px', align: 'right' },
  { key: 'buyerName', label: 'Buyer Name', type: 'text', width: '160px' },
  { key: 'buyerPhone', label: 'Buyer Phone', type: 'text', width: '150px' },
  { key: 'buyerEmail', label: 'Buyer Email', type: 'text', width: '200px' },
  { key: 'agesNote', label: 'Ages', type: 'text', width: '220px' },
  { key: 'notes', label: 'Notes', type: 'text', width: '200px' },
  { key: 'helloReference', label: 'Hello Reference', type: 'text', width: '140px' },
];

// Convert an ISO / Date value to the yyyy-MM-dd format that <input type="date"> needs.
const toDateInput = (value?: string | Date): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

// Profit is only meaningful once a Price Bought has been entered.
const hasPriceBought = (order: Order): boolean => {
  const v = order.priceBought;
  return v !== null && v !== undefined && (v as any) !== '';
};

// ---- Sourcing status ------------------------------------------------------
// How much of a sale we've actually bought in. A 3-ticket sale might be half-sourced — a pair
// secured from one seller, the single still open — and that order is NOT done: it still needs
// tickets, so it has to keep showing up under the "Needed" filter alongside the untouched ones.
//
// `ticketsBought` is the only new input, and blank means "not tracked", which keeps every order
// that predates the field behaving exactly as before: a supplier = fully bought, none = nothing
// bought. Fill it in ONLY for a part-bought sale.
type SourcingStatus = 'none' | 'partial' | 'done';

const ticketsSourced = (order: Order): number => {
  const qty = Number(order.quantity) || 0;
  const raw = (order as any).ticketsBought;
  if (raw !== null && raw !== undefined && raw !== '' && !isNaN(Number(raw))) return Number(raw);
  return String((order as any).boughtFrom ?? '').trim() ? qty : 0; // untracked → infer from supplier
};

const sourcingStatus = (order: Order): SourcingStatus => {
  const qty = Number(order.quantity) || 0;
  const got = ticketsSourced(order);
  if (got <= 0) return 'none';
  return qty > 0 && got < qty ? 'partial' : 'done';
};

// "still needs 1" — what's left to buy on a part-bought sale.
const ticketsOutstanding = (order: Order): number =>
  Math.max(0, (Number(order.quantity) || 0) - ticketsSourced(order));

// Fallback FX → GBP, only used if an order has no stored fxRate (shouldn't happen — the importers
// stamp order.fxRate with the rate at import time, refreshed daily). GBP passes through unchanged.
const FX_TO_GBP: Record<string, number> = { GBP: 1, EUR: 0.85, USD: 0.79 };
const CUR_SYMBOL: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' };

// The order's currency→GBP rate, locked at import time (order.fxRate). Falls back to the static table.
const rateFor = (order: Order): number =>
  Number((order as any).fxRate) || FX_TO_GBP[(order as any).currency as string] || 1;

const isWorldCup = (order: Order): boolean => /world cup/i.test((order as any).eventName || '');

// GBP profit for ALL orders (sale total → GBP via the rate locked at import — World Cup carries 0.75;
// Price Bought is always stored in GBP). This is what the footer total sums, for every platform.
const computeProfit = (order: Order): number => {
  const total = Number(order.totalPriceGBP) || 0;
  const bought = Number(order.priceBought) || 0;
  return total * rateFor(order) - bought;
};
const computeProfitGBP = (order: Order): number => computeProfit(order);

// Native USD profit for World Cup rows: USD sale − Price Bought back-converted from GBP to USD.
const computeProfitUSD = (order: Order): number => {
  const total = Number(order.totalPriceGBP) || 0; // WC sale is stored in USD
  const boughtUsd = (Number(order.priceBought) || 0) / (rateFor(order) || 1);
  return total - boughtUsd;
};

// Row display: World Cup shows BOTH "$usd / £gbp"; every other platform just "£gbp".
const formatProfitCell = (order: Order): string => {
  if (!hasPriceBought(order)) return '';
  const gbp = formatMoneyCur(computeProfit(order), 'GBP');
  return isWorldCup(order)
    ? `${formatMoneyCur(computeProfitUSD(order), 'USD')} / ${gbp}`
    : gbp;
};

const computePricePerTicket = (order: Order): number => {
  const total = Number(order.totalPriceGBP) || 0;
  const qty = Number(order.quantity) || 0;
  return qty > 0 ? total / qty : total;
};

// Sale total converted to GBP (uses the rate locked at import), for the always-visible
// "Total GBP Price" column.
const computeTotalGBP = (order: Order): number =>
  (Number(order.totalPriceGBP) || 0) * rateFor(order);

const formatMoney = (n: number): string =>
  n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatMoneyCur = (n: number, currency: string): string =>
  (CUR_SYMBOL[currency] || '') + formatMoney(n);

// ---- Column filtering (Google-Sheets style) -------------------------------
// Money-style columns are grouped/displayed using formatMoney so the filter
// list shows clean values. `profit` is a computed column.
const MONEY_KEYS = new Set<string>(['totalPriceGBP', 'priceBought', 'profit']);
const COLUMN_TYPE: Record<string, FieldType> = COLUMNS.reduce(
  (acc, c) => ({ ...acc, [c.key as string]: c.type }),
  {} as Record<string, FieldType>
);
// Columns actually rendered in the desktop table.
const VISIBLE_COLUMNS: ColumnDef[] = COLUMNS.filter((c) => !c.hidden);

// All filterable columns: every data column plus the computed Profit column.
const FILTER_KEYS: string[] = [...COLUMNS.map((c) => c.key as string), 'profit', 'waStatus', 'delivery'];

// UI-only platform relabelling. Stored values are untouched; this maps only what's shown, filtered,
// and typed. `toPlatformStore` maps an edited label back so the DB keeps the canonical value.
// Sales made directly rather than through a marketplace — to a supplier, or to a private client.
// Stored as an ordinary platform value so the chips, column filters, totals, Copy and the help
// bot's platform filter all treat it like any other source with no special-casing.
const PLATFORM_PRIVATE = 'Private';

const PLATFORM_LABEL: Record<string, string> = { LiveFootballTickets: 'Live', Tixstock: 'TX' };
const PLATFORM_STORE: Record<string, string> = { Live: 'LiveFootballTickets', TX: 'Tixstock' };
const toPlatformLabel = (v: string): string => PLATFORM_LABEL[v] ?? v;
const toPlatformStore = (v: string): string => PLATFORM_STORE[v] ?? v;

// ---- Seasons ---------------------------------------------------------------
// A season is a date window on the EVENT date. Boundaries are exclusive-end, so a fixture on
// 1 Aug 2026 belongs to 2026-27. Add a row here when a new season starts and set CURRENT_SEASON.
//
// Season selection is deliberately NOT part of `filters`: "Clear filters" resets it to the CURRENT
// season rather than revealing everything, so last season's World Cup and concert orders only ever
// appear when that season is ticked on purpose.
const SEASONS: { id: string; label: string; from?: string; to?: string }[] = [
  { id: '2025-26', label: '25/26', to: '2026-08-01' },
  { id: '2026-27', label: '26/27', from: '2026-08-01' },
];
const CURRENT_SEASON = '2026-27';
const defaultSeasons = () => new Set<string>([CURRENT_SEASON]);
// Orders with no event date (a handful of private sales) are never hidden by a season — they have
// no date to place them, and silently dropping them would make them unreachable.
const seasonOf = (eventDate: unknown): string | null => {
  const d = eventDate ? new Date(eventDate as string) : null;
  if (!d || isNaN(d.getTime())) return null;
  const iso = d.toISOString().slice(0, 10);
  for (const s of SEASONS) if ((!s.from || iso >= s.from) && (!s.to || iso < s.to)) return s.id;
  return null;
};

// Columns whose filter values keep their original case — they are also rendered as chips.
const CASE_SENSITIVE_FILTER_KEYS = new Set(['eventName', 'platform']);

// Canonical string used both for grouping distinct values and for matching.
// One fixture = its name AND its date. Two games can share a name in the same season (a league tie
// and a cup tie, a rescheduled fixture), so a name alone is not an identity: filtering on it pulled
// in sales from a game months away. Not a real column — it exists only so the Games chips have
// something exact to filter on, and it is deliberately absent from COLUMNS so no filter menu shows it.
// One fixture, one chip — whatever each platform calls it. Liverpool v Atlético arrived as
// "Liverpool vs Atletico Madrid" (Live), "Liverpool Fc Vs Atlético De Madrid - Champions League
// 2026-2027" (Tixstock), "Liverpool vs Atlético de Madrid - Champions League 2026-2027" (StubHub)
// and split into four chips. Strip accents, case, the competition suffix, club suffixes and
// "de", so those spellings share one identity; the chip is labelled with the most common one.
const fixtureKeyOf = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+-\s+.*$/, '')
    .replace(/\b(?:vs|v)\.?(?=\s)/g, ' vs ')
    .replace(/\b(?:fc|afc|fk|cf|sc|de)\b/g, ' ')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const gameKeyOf = (order: Order): string => {
  const name = String((order as any).eventName ?? '');
  const raw = (order as any).eventDate;
  return name + '||' + (raw ? toDateInput(raw) : '');
};

const getFilterValue = (order: Order, key: string): string => {
  if (key === 'gameKey') return gameKeyOf(order);
  if (key === 'profit') return formatProfitCell(order);
  if (key === 'waStatus') return String((order as any).waStatus ?? '');
  if (key === 'delivery') return String((order as any).delivery ?? '');
  const type = COLUMN_TYPE[key];
  const raw = (order as any)[key];
  if (type === 'date') return toDateInput(raw);
  if (MONEY_KEYS.has(key))
    return raw == null || raw === '' ? '' : formatMoney(Number(raw));
  if (type === 'number')
    return raw == null || raw === '' ? '' : String(Number(raw));
  if (key === 'platform') return raw == null ? '' : toPlatformLabel(String(raw));
  if (raw == null) return '';
  // Free-text columns are typed by hand, so the same value arrives in several cases ("site",
  // "Site", "SITE"; "danny"/"Danny") and used to split into separate filter entries. Fold them to
  // lowercase so one value = one filter, and the menus read lowercase.
  // eventName and platform are deliberately NOT folded: their values are rendered directly as the
  // Games and Platform chips, where lowercasing would just look broken (they have no case clashes).
  const str = String(raw);
  return CASE_SENSITIVE_FILTER_KEYS.has(key) ? str : str.toLowerCase();
};

// Match-name matching, as a reusable predicate. Without "vs" it stays loose — "chelsea" finds every
// Chelsea fixture. WITH "vs" it is DIRECTIONAL: "newcastle vs liverpool" must not also return
// "Liverpool vs Newcastle", which is a different game at a different ground. Word order within each
// side still doesn't matter. Mirrors the help bot's filter so both screens agree.
// Returns null when the box is empty (nothing to filter on).
const matchNamePredicate = (raw: string): ((o: Order) => boolean) | null => {
  const mq = raw.trim().toLowerCase();
  if (!mq) return null;
  const VS = /\s+vs?\.?\s+/;
  const nameOf = (o: Order) => String((o as any).eventName ?? '').toLowerCase();
  if (VS.test(mq)) {
    const [qHome, ...qRest] = mq.split(VS);
    const homeWords = qHome.split(/\s+/).filter(Boolean);
    const awayWords = qRest.join(' ').split(/\s+/).filter(Boolean);
    return (o) => {
      const parts = nameOf(o).split(VS);
      if (parts.length < 2) return false;
      const home = parts[0];
      const away = parts.slice(1).join(' ');
      return homeWords.every((w) => home.includes(w)) && awayWords.every((w) => away.includes(w));
    };
  }
  const words = mq.split(/\s+/).filter(Boolean);
  return (o) => {
    const name = nameOf(o);
    return words.every((w) => name.includes(w));
  };
};

// Blanks sort last; numeric values sort numerically, everything else by locale.
const compareFilterValues = (a: string, b: string): number => {
  if (a === b) return 0;
  if (a === '') return 1;
  if (b === '') return -1;
  const na = Number(a.replace(/,/g, ''));
  const nb = Number(b.replace(/,/g, ''));
  if (!isNaN(na) && !isNaN(nb)) return na - nb;
  return a.localeCompare(b);
};

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  // Tracks the value of whichever cell is actively being typed into.
  // Keyed as `${rowId}_${colKey}`. Only this tiny map updates on every keystroke —
  // the full `orders` array (and all memos that depend on it) only updates on blur.
  const [editingCells, setEditingCells] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; color: string } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  // "Add Private" picks the match from the fixtures that already have orders, so the name and date
  // come out exactly as the imports spell them (and the game chips / filters group it with the rest)
  // instead of being typed free-hand (user request 2026-10-04). Free text is still offered as a
  // fallback for a match with no orders yet.
  const [privatePick, setPrivatePick] = useState(false);
  const [privatePickQuery, setPrivatePickQuery] = useState('');
  // Row pinned by a click: it keeps the highlight while scrolling sideways, so you don't lose
  // which row you were reading. Click again (or another row) to move/release it.
  const [pinnedRowId, setPinnedRowId] = useState<string | null>(null);
  // Rejected-orders record: hidden from the table, kept in rejected_orders, shown in this modal.
  const [rejectedList, setRejectedList] = useState<any[] | null>(null);

  // Reject a Hello order: confirm → Hello status 'rejected' → row moves to the rejected record.
  const rejectOrder = async (order: Order) => {
    const id = rowId(order);
    const msg =
      `Reject Hello order ${(order as any).orderId} — ${(order as any).buyerName || 'no name'}, ` +
      `${(order as any).eventName}?\n\nThis sets the order to REJECTED on HelloTickets (like the ` +
      `hello app does) and removes it from this screen. A record is kept under "Rejected".`;
    if (!window.confirm(msg)) return;
    try {
      await orderService.reject(id);
      setOrders((prev) => prev.filter((o) => rowId(o) !== id));
      setToast({ message: `Order ${(order as any).orderId} rejected on Hello and archived`, color: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message || 'Reject failed — nothing was removed', color: 'danger' });
    }
  };

  const openRejected = async () => {
    try {
      setRejectedList(await orderService.listRejected());
    } catch {
      setToast({ message: 'Could not load rejected orders', color: 'danger' });
    }
  };

  // Confirm a pending Hello order, or mark a confirmed one delivered. The server checks the live
  // Hello status and refuses the wrong transition, so this stays honest even though the row itself
  // doesn't carry the platform status. On deliver, the row picks up the "Delivered (manual)" label.
  const helloAction = async (order: Order, action: 'confirm' | 'deliver') => {
    const id = rowId(order);
    const verb = action === 'confirm' ? 'Confirm' : 'Mark delivered';
    if (!window.confirm(`${verb} Hello order ${(order as any).orderId} — ${(order as any).eventName}?`)) return;
    try {
      const res = await orderService.helloStatus(id, action);
      const newHs = action === 'confirm' ? 'confirmed' : 'delivered';
      setOrders((prev) =>
        prev.map((o) =>
          rowId(o) === id
            ? ({
                ...o,
                helloStatus: newHs,
                ...(action === 'deliver'
                  ? { delivery: 'Delivered', deliveryDetail: 'Delivered (manual)', deliveryStage: 'delivered' }
                  : {}),
              } as Order)
            : o,
        ),
      );
      setToast({
        message: res.already
          ? `Order ${(order as any).orderId} was already ${action === 'confirm' ? 'confirmed' : 'delivered'}`
          : `Order ${(order as any).orderId}: ${res.from} → ${res.to} on Hello`,
        color: 'success',
      });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message || `${verb} failed`, color: 'danger' });
    }
  };
  // Per-column filters: a column with an entry shows only its selected values.
  // No entry = column is unfiltered. Multiple columns combine with AND.
  const [filters, setFilters] = useState<Record<string, Set<string>>>({});
  // Sourcing filter, on top of the column filters. "Needed" deliberately includes PART-bought
  // orders — a sale with 2 of 3 tickets secured still needs sourcing, so hiding it there would be
  // the whole point of the field lost. "Partial" isolates just those.
  const [boughtFromMode, setBoughtFromMode] = useState<'all' | 'empty' | 'partial' | 'filled'>('all');

  // ── Realtime collaboration (Socket.IO): live updates + cell locking ──
  const socketRef = useRef<Socket | null>(null);
  const mySocketIdRef = useRef<string | undefined>(undefined); // my connection id (per-tab)
  // Cells currently being edited by someone, keyed `${orderId}::${field}` → who (name + their connection).
  const [lockedCells, setLockedCells] = useState<Record<string, { by: string; socketId: string }>>({});
  const focusedCellRef = useRef<string | null>(null); // the cell key I'm currently editing
  const [filterMenu, setFilterMenu] = useState<{ key: string; event: Event } | null>(null);
  const [filterSearch, setFilterSearch] = useState('');
  // Columns the user has expanded to fit their content (keyed by column key).
  const [expandedCols, setExpandedCols] = useState<Record<string, boolean>>({ eventName: true, category: true });
  const [summaryMenu, setSummaryMenu] = useState<{ key: string; event: Event } | null>(null);

  // Merge WhatsApp/delivery status onto the rows already in state. Used by the initial load and by
  // the refresh button — it never replaces `orders`, so edits, scroll position and every filter
  // stay exactly as they are.
  const applyWaStatuses = useCallback((map: Record<string, any>) => {
    setOrders((prev) =>
      prev.map((o) => {
        const st = map[String((o as any).orderId ?? '')];
        return st
          ? ({
              ...o,
              waStatus: st.label,
              waStatusCode: st.code,
              // Three filterable values: 'Delivered', 'Waiting for buyer' (tickets are SAVED at the
              // gate — instructions sent, holding for the buyer's confirmation) and 'Not delivered'
              // (nothing loaded yet — still OUR move).
              delivery: st.delivered
                ? 'Delivered'
                : st.stage === 'awaiting_approval'
                ? 'Waiting for buyer'
                : 'Not delivered',
              deliveryDetail: st.stageLabel,
              deliveryStage: st.stage,
              deliveryWarning: st.receiptWarning,
            } as Order)
          : o;
      }),
    );
  }, []);

  // Re-check delivery status for the rows ON SCREEN only. Deliberately manual: the table would be
  // unusable if it polled, and after sending a batch of tickets the question is always "what is
  // still to send" for the set already filtered down to.
  const [statusRefreshing, setStatusRefreshing] = useState(false);
  const refreshStatuses = async () => {
    const ids = visibleOrders.map((o) => String((o as any).orderId ?? '')).filter(Boolean);
    if (!ids.length) return;
    setStatusRefreshing(true);
    try {
      applyWaStatuses(await getOrderWaStatusesFor(ids));
      setToast({ message: `Delivery status refreshed for ${ids.length} order${ids.length === 1 ? '' : 's'}`, color: 'success' });
    } catch {
      setToast({ message: 'Could not refresh delivery status', color: 'danger' });
    } finally {
      setStatusRefreshing(false);
    }
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getAll();
      setOrders(data);
      // WhatsApp per-order status (message/instructions/tickets receipts) — merged onto the rows
      // so the computed column + its filter behave like any other field. Non-blocking: the table
      // renders without it and fills in when it lands.
      getOrderWaStatuses()
        .then((map) => applyWaStatuses(map))
        .catch(() => {});
    } catch (err) {
      console.error('Failed to load orders:', err);
      setToast({ message: 'Failed to load orders', color: 'danger' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const rowId = (order: Order) => (order._id || order.id) as string;

  // Update a single cell locally so the input stays responsive.
  const updateLocal = (id: string, key: keyof Order, value: any) => {
    setOrders((prev) =>
      prev.map((o) => (rowId(o) === id ? { ...o, [key]: value } : o))
    );
  };

  // Connect to the realtime server once: apply live order changes (edits + imports) and track locks.
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      // Fresh Firebase token on every (re)connect.
      auth: (cb: (data: { token: string }) => void) => {
        const u = auth.currentUser;
        if (!u) return cb({ token: '' });
        u.getIdToken().then((t) => cb({ token: t })).catch(() => cb({ token: '' }));
      },
    });
    socketRef.current = socket;
    socket.on('connect', () => {
      mySocketIdRef.current = socket.id;
    });

    socket.on('order:changed', ({ op, id, doc }: { op: string; id: string; doc?: any }) => {
      setOrders((prev) => {
        if (op === 'delete') return prev.filter((o) => rowId(o) !== id);
        const idx = prev.findIndex((o) => rowId(o) === id);
        if (idx === -1) return doc ? [doc as Order, ...prev] : prev;
        const next = [...prev];
        next[idx] = { ...prev[idx], ...(doc as Order) };
        return next;
      });
    });

    socket.on('locks:init', (locks: { orderId: string; field: string; by: string; socketId: string }[]) => {
      const map: Record<string, { by: string; socketId: string }> = {};
      for (const l of locks) map[`${l.orderId}::${l.field}`] = { by: l.by, socketId: l.socketId };
      setLockedCells(map);
    });
    socket.on('cell:locked', ({ orderId, field, by, socketId }: { orderId: string; field: string; by: string; socketId: string }) =>
      setLockedCells((p) => ({ ...p, [`${orderId}::${field}`]: { by, socketId } })),
    );
    socket.on('cell:unlocked', ({ orderId, field }: { orderId: string; field: string }) =>
      setLockedCells((p) => {
        const n = { ...p };
        delete n[`${orderId}::${field}`];
        return n;
      }),
    );
    socket.on('cell:lock:denied', ({ orderId, field, by }: { orderId: string; field: string; by: string }) => {
      if (focusedCellRef.current === `${orderId}::${field}`) {
        (document.activeElement as HTMLElement | null)?.blur();
      }
      setToast({ message: `That cell is being edited by ${by}`, color: 'warning' });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lockCell = (id: string, field: string) => socketRef.current?.emit('cell:lock', { orderId: id, field });
  const unlockCell = (id: string, field: string) => socketRef.current?.emit('cell:unlock', { orderId: id, field });
  // Returns the locker if the cell is held by ANOTHER connection (not my tab), else null.
  const lockedByOther = (id: string, field: string) => {
    const l = lockedCells[`${id}::${field}`];
    return l && l.socketId !== mySocketIdRef.current ? l : null;
  };

  // Persist a single field to the server and merge the canonical result
  // (which includes the recomputed profit) back into local state.
  const persist = async (id: string, key: keyof Order, rawValue: any, type: FieldType) => {
    let value: any = rawValue;
    if (type === 'number') {
      if (rawValue === '' || rawValue == null) {
        value = null; // keep empty rather than forcing 0
      } else {
        value = Number(rawValue);
        if (isNaN(value)) value = null;
      }
    }

    try {
      setSaving(true);
      const updated = await orderService.update(id, { [key]: value });
      setOrders((prev) => prev.map((o) => (rowId(o) === id ? { ...o, ...updated } : o)));
    } catch (err) {
      console.error('Failed to save order:', err);
      setToast({ message: 'Failed to save change', color: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  // Next free PRIV-nnn. Platform orders arrive with an id from the platform; a private sale has
  // none, and a blank id means the row shows "(no id)" and the order-id box can never find it.
  // Numbering off the rows already loaded can in principle collide if two people add at the same
  // moment — the id is editable, so that's a rename rather than a problem.
  const nextPrivateId = (): string => {
    let max = 0;
    for (const o of orders) {
      const m = /^PRIV-(\d+)$/i.exec(String((o as any).orderId ?? '').trim());
      if (m) max = Math.max(max, Number(m[1]));
    }
    return `PRIV-${String(max + 1).padStart(3, '0')}`;
  };

  // Jump to Messages with this buyer's chat open and the delivery dialog up. The buyer phone is
  // passed digits-only — conversation threads are keyed that way (wa_id has no '+').
  const deliverFromOrder = (order: Order) => {
    const digits = String((order as any).buyerPhone ?? '').replace(/\D/g, '');
    if (!digits) return;
    window.location.href = `/messages?deliver=${digits}`;
  };

  // Same jump, but just the chat — no delivery dialog.
  const openChatFromOrder = (order: Order) => {
    const digits = String((order as any).buyerPhone ?? '').replace(/\D/g, '');
    if (!digits) return;
    window.location.href = `/messages?chat=${digits}`;
  };

  const addOrder = async (privateSale = false, preset: Record<string, unknown> = {}) => {
    try {
      setSaving(true);
      // A private sale is one we make directly — to a supplier or a private client — rather than
      // through a marketplace. It's an ordinary order in every other respect, so it just carries
      // `platform: 'Private'`, which makes every existing filter, chip and total work on it
      // unchanged. Dated today so it sorts to the top like any freshly-entered sale.
      const created = await orderService.create(
        privateSale
          ? {
              quantity: 1,
              platform: PLATFORM_PRIVATE,
              orderId: nextPrivateId(),
              orderDate: new Date().toISOString().slice(0, 10),
              ...preset,
            }
          : { quantity: 1 },
      );
      // The change-stream broadcast for this insert can beat the POST response back, in which
      // case the row is already in the list — merge into it rather than prepending a second copy.
      setOrders((prev) =>
        prev.some((o) => rowId(o) === rowId(created))
          ? prev.map((o) => (rowId(o) === rowId(created) ? { ...o, ...created } : o))
          : [created, ...prev],
      );
      if (privateSale) setToast({ message: `Private sale ${created.orderId} added`, color: 'success' });
    } catch (err) {
      console.error('Failed to add order:', err);
      setToast({ message: 'Failed to add order', color: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    try {
      setSaving(true);
      await orderService.delete(id);
      setOrders((prev) => prev.filter((o) => rowId(o) !== id));
    } catch (err) {
      console.error('Failed to delete order:', err);
      setToast({ message: 'Failed to delete order', color: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  // Distinct present values per column (across all rows), for the filter menus.
  const distinctByKey = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const key of FILTER_KEYS) {
      const set = new Set<string>();
      for (const o of orders) set.add(getFilterValue(o, key));
      map[key] = Array.from(set).sort(compareFilterValues);
    }
    return map;
  }, [orders]);

  // One-shot order lookup: while the quick-find box has text, it OVERRIDES every other filter — so
  // finding an order is type → done, no clearing the current filter set first (which stays intact
  // and comes back the moment the box is emptied). Matches order id or Hello reference, substring.
  const [quickFind, setQuickFind] = useState('');
  // Match-name quick filter. Unlike the order-id box (a unique lookup, so it overrides), this one
  // COMPOSES with everything else — "chelsea" + the Live chip + Bought-From Empty all AND together.
  const [matchFind, setMatchFind] = useState('');
  // Date-range filters (event date and/or order date), yyyy-MM-dd strings straight from the date
  // inputs. Empty string = that bound is open. Compose (AND) with everything else. An order missing
  // the date is hidden while a range on that date is active — "events in August" shouldn't
  // silently include undated rows.
  const [seasons, setSeasons] = useState<Set<string>>(defaultSeasons);
  const [dateRanges, setDateRanges] = useState({ eventFrom: '', eventTo: '', orderFrom: '', orderTo: '' });
  const [datesOpen, setDatesOpen] = useState(false);
  const dateRangeCount =
    (dateRanges.eventFrom || dateRanges.eventTo ? 1 : 0) + (dateRanges.orderFrom || dateRanges.orderTo ? 1 : 0);

  // THE row filter — every active control in one place: column filters, the match box, the sourcing
  // toggle. `exceptKey` omits one column's own filter, which is what a filter menu needs (a menu
  // must offer every value that column could still take, not just the ones already ticked).
  //
  // The filter MENUS run through this too, so they can only ever offer values that actually exist
  // in the rows on screen — pick a game in the match box and the Category menu lists that game's
  // categories, not all 200-odd across every fixture.
  const filterRows = useCallback(
    (exceptKey: string | null): Order[] => {
      // The order-id box is a one-shot lookup and deliberately OVERRIDES everything else.
      const q = quickFind.trim().toLowerCase();
      if (q) {
        return orders.filter(
          (o) =>
            String((o as any).orderId ?? '').toLowerCase().includes(q) ||
            String((o as any).helloReference ?? '').toLowerCase().includes(q),
        );
      }
      const activeKeys = Object.keys(filters).filter((k) => k !== exceptKey);
      let rows =
        activeKeys.length === 0
          ? orders
          : orders.filter((o) => activeKeys.every((key) => filters[key].has(getFilterValue(o, key))));
      const matches = matchNamePredicate(matchFind);
      if (matches) rows = rows.filter(matches);
      // Season window. Undated orders always survive (see seasonOf).
      if (seasons.size) {
        rows = rows.filter((o) => {
          const sid = seasonOf((o as any).eventDate);
          return sid === null || seasons.has(sid);
        });
      }
      if (dateRangeCount > 0) {
        // yyyy-MM-dd strings compare correctly as plain strings — no Date parsing needed here.
        const { eventFrom, eventTo, orderFrom, orderTo } = dateRanges;
        rows = rows.filter((o) => {
          if (eventFrom || eventTo) {
            const ev = toDateInput((o as any).eventDate);
            if (!ev || (eventFrom && ev < eventFrom) || (eventTo && ev > eventTo)) return false;
          }
          if (orderFrom || orderTo) {
            const od = toDateInput((o as any).orderDate);
            if (!od || (orderFrom && od < orderFrom) || (orderTo && od > orderTo)) return false;
          }
          return true;
        });
      }
      if (boughtFromMode !== 'all') {
        rows = rows.filter((o) => {
          const s = sourcingStatus(o);
          if (boughtFromMode === 'filled') return s === 'done';
          if (boughtFromMode === 'partial') return s === 'partial';
          return s !== 'done'; // "Needed" = nothing bought yet OR only part-bought
        });
      }
      return rows;
    },
    [orders, filters, boughtFromMode, quickFind, matchFind, dateRanges, dateRangeCount, seasons],
  );

  const visibleOrders = useMemo(() => filterRows(null), [filterRows]);

  // Fixtures with orders on file: one entry per match + date, upcoming first. Yesterday's games
  // are kept (a private sale is often entered the morning after); older ones are left out.
  const knownMatches = useMemo(() => {
    const cutoff = new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const map = new Map<string, { name: string; date: string; n: number }>();
    for (const o of orders) {
      const name = String((o as any).eventName ?? '').trim();
      if (!name) continue;
      const date = toDateInput((o as any).eventDate);
      if (date && date < cutoff) continue;
      const key = name + '||' + date;
      const e = map.get(key);
      if (e) e.n += 1;
      else map.set(key, { name, date, n: 1 });
    }
    return [...map.values()].sort((a, b) => (a.date || '9999').localeCompare(b.date || '9999') || a.name.localeCompare(b.name));
  }, [orders]);
  const privatePickMatches = useMemo(() => {
    const q = privatePickQuery.trim().toLowerCase();
    if (!q) return knownMatches;
    const words = q.split(/\s+/).filter(Boolean);
    return knownMatches.filter((m) => words.every((w) => m.name.toLowerCase().includes(w)));
  }, [knownMatches, privatePickQuery]);
  const pickPrivateMatch = (m: { name: string; date: string } | null) => {
    setPrivatePick(false);
    const typed = privatePickQuery.trim();
    setPrivatePickQuery('');
    if (m) void addOrder(true, { eventName: m.name, ...(m.date ? { eventDate: m.date } : {}) });
    else void addOrder(true, typed ? { eventName: typed } : {});
  };

  // Event months for the one-click "this month's events" chips in the Dates panel. Only months that
  // actually hold events in the loaded orders (so the row stays short), oldest first; the range they
  // set is the 1st to the last day of that month, on EVENT date (user request 2026-09-29).
  const eventMonths = useMemo(() => {
    const seen = new Set<string>();
    for (const o of orders) {
      const ev = toDateInput((o as any).eventDate);
      if (ev) seen.add(ev.slice(0, 7));
    }
    return [...seen].sort();
  }, [orders]);
  const monthRange = (ym: string) => {
    const [y, m] = ym.split('-').map(Number);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, '0')}` };
  };
  const activeMonth = eventMonths.find((ym) => {
    const r = monthRange(ym);
    return dateRanges.eventFrom === r.from && dateRanges.eventTo === r.to;
  });
  const multiYear = new Set(eventMonths.map((ym) => ym.slice(0, 4))).size > 1;
  const monthLabel = (ym: string, long = false) => {
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1, 1));
    return long
      ? d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
      : d.toLocaleDateString('en-GB', { month: 'short', ...(multiYear ? { year: '2-digit' } : {}), timeZone: 'UTC' });
  };

  // Platform chips. "Private" is pinned in even when no private sale exists yet, so the filter is
  // there to click from day one rather than appearing only once you've entered one.
  const platformChips = useMemo(() => {
    const list = distinctByKey['platform'] || [];
    return list.includes(PLATFORM_PRIVATE) ? list : [...list, PLATFORM_PRIVATE];
  }, [distinctByKey]);

  // Standing count of part-bought sales across ALL orders (not just the visible ones) — it's a
  // "you still owe tickets on N sales" badge, so a game filter shouldn't hide it.
  const partialCount = useMemo(
    () => orders.reduce((n, o) => n + (sourcingStatus(o) === 'partial' ? 1 : 0), 0),
    [orders],
  );

  // The 20 SOONEST upcoming games — quick-filter chips, one per FIXTURE rather than per name.
  //
  // Two things have to be true at once. A club can play the same opponent twice in a season (a
  // league tie and a cup tie), and those are different games whose sales must not be mixed — a name
  // alone was pulling in orders from a game months away. But ONE game also arrives under several
  // dates, because platforms record kickoff in their own timezone or with a placeholder: Man Utd vs
  // Man City sits on both 12 and 13 Sep, Liverpool vs Tottenham on 14, 15 and 16. Keying strictly on
  // the exact date would split a single game into several chips, each showing part of its sales.
  //
  // So dates within DRIFT_DAYS of each other collapse into one fixture and anything further apart
  // stays separate. Observed drift is 1–2 days; the December Liverpool/Tottenham tie is three months
  // out, so there is no ambiguity to resolve here. The chip carries EVERY exact key in its cluster,
  // which is what the filter matches on — no change needed to the filter engine itself.
  const closestGames = useMemo(() => {
    const DRIFT_DAYS = 3;
    const now = Date.now();
    const cutoff = now - 24 * 60 * 60 * 1000; // future, or at most 1 day past

    // exact fixture (name + its own date) -> how many orders sit on it
    const exact = new Map<string, { name: string; date: number; n: number }>();
    for (const o of orders) {
      const name = getFilterValue(o, 'eventName');
      if (!name) continue;
      const t = o.eventDate ? new Date(o.eventDate).getTime() : NaN;
      if (Number.isNaN(t)) continue;
      const key = getFilterValue(o, 'gameKey');
      const prev = exact.get(key);
      if (prev) prev.n += 1;
      else exact.set(key, { name, date: t, n: 1 });
    }

    // group by fixture identity (name variants collapsed), then split each fixture's dates into
    // clusters no more than DRIFT_DAYS apart
    const byName = new Map<string, { key: string; name: string; date: number; n: number }[]>();
    for (const [key, g] of exact) {
      const fixture = fixtureKeyOf(g.name);
      const list = byName.get(fixture) || [];
      list.push({ key, name: g.name, date: g.date, n: g.n });
      byName.set(fixture, list);
    }

    const games: { key: string; keys: string[]; name: string; date: number }[] = [];
    for (const [, list] of byName) {
      list.sort((a, b) => a.date - b.date);
      let cluster: typeof list = [];
      const flush = () => {
        if (!cluster.length) return;
        // Show the date most of the orders agree on — the outliers are the platforms that drifted.
        const main = cluster.reduce((best, c) => (c.n > best.n ? c : best), cluster[0]);
        // ...and label the chip with the spelling most of its orders carry.
        const votes = new Map<string, number>();
        for (const c of cluster) votes.set(c.name, (votes.get(c.name) || 0) + c.n);
        const name = [...votes.entries()].reduce((best, e) => (e[1] > best[1] ? e : best))[0];
        games.push({ key: cluster[0].key, keys: cluster.map((c) => c.key), name, date: main.date });
        cluster = [];
      };
      for (const item of list) {
        if (cluster.length && item.date - cluster[cluster.length - 1].date > DRIFT_DAYS * 864e5) flush();
        cluster.push(item);
      }
      flush();
    }

    return games.filter((g) => g.date >= cutoff).sort((a, b) => a.date - b.date).slice(0, 20);
  }, [orders]);

  // Longest value (in characters) per column, used to size an expanded column
  // so its content isn't cut off.
  const maxLenByKey = useMemo(() => {
    const map: Record<string, number> = {};
    for (const col of COLUMNS) {
      let max = col.label.length;
      for (const o of orders) {
        const len = getFilterValue(o, col.key as string).length;
        if (len > max) max = len;
      }
      map[col.key as string] = max;
    }
    return map;
  }, [orders]);

  // Style for a column header/cell: default width, or a content-fit width when
  // the column is expanded (capped so it never gets absurdly wide).
  const colStyle = (col: ColumnDef): React.CSSProperties => {
    // orderId is pinned (sticky left) — its width must stay EXACTLY 130px so eventName's sticky
    // offset (left-[130px]) always lines up, expansion or not.
    if (col.key === 'orderId') return { minWidth: col.width, width: col.width, maxWidth: col.width };
    if (!expandedCols[col.key as string]) return { minWidth: col.width };
    const ch = Math.min(80, Math.max(12, (maxLenByKey[col.key as string] || 12) + 4));
    return { minWidth: `${ch}ch`, width: `${ch}ch` };
  };

  const toggleExpand = (key: string) =>
    setExpandedCols((prev) => ({ ...prev, [key]: !prev[key] }));

  // Mobile filter sheet: the card view has no column headers, so phones get a bottom sheet that
  // reaches the same per-column value filters. `filterSheetKey` is the column whose values are open
  // (null = the column list). Shares all filter state/handlers with the desktop popovers.
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [filterSheetKey, setFilterSheetKey] = useState<string | null>(null);

  // Values to show in the OPEN column's filter menu — desktop popover or mobile sheet, whichever is
  // active: only values still present under every OTHER active control (Excel-style cascading).
  // That includes the match box and the sourcing toggle, not just the column filters — otherwise
  // narrowing to one game still offers every category in the book.
  const availableFilterValues = useMemo(() => {
    const key = filterMenu?.key ?? filterSheetKey;
    if (!key) return [] as string[];
    const set = new Set<string>();
    for (const o of filterRows(key)) set.add(getFilterValue(o, key));
    // STRICTLY the cascade — never union the selected set. Doing that re-listed every category in
    // the database after a Clear, because Clear used to store "all global values except the
    // visible ones". A selection stranded by another filter is recovered with "Reset column"
    // below, not by polluting the list.
    return Array.from(set).sort(compareFilterValues);
  }, [filterRows, filterMenu, filterSheetKey]);

  // The subset of availableFilterValues currently shown under the search box (same label logic as the
  // menu list). Select-all / Clear operate on THIS set, so they only affect the searched values.
  const visibleFilterValues = useMemo(() => {
    const q = filterSearch.toLowerCase();
    return availableFilterValues.filter((v) => {
      const label = v === '' ? '(blanks)' : v.toLowerCase();
      return label.includes(q);
    });
  }, [availableFilterValues, filterSearch]);

  const isColumnFiltered = (key: string) => !!filters[key];
  const isFilterChecked = (key: string, value: string) =>
    filters[key] ? filters[key].has(value) : true;

  // All three mutators work off the CASCADE (availableFilterValues), not the global distinct list.
  // Seeding from the global list is what produced sets of ~200 categories when a single game was
  // selected; seeding from what's actually on screen keeps every filter set small and meaningful.
  const toggleFilterValue = (key: string, value: string) => {
    setFilters((prev) => {
      const shown = availableFilterValues;
      const current = prev[key] ? new Set(prev[key]) : new Set(shown);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      const next = { ...prev };
      // Everything in view selected → no constraint within this view.
      if (current.size >= shown.length) delete next[key];
      else next[key] = current;
      return next;
    });
  };

  // Select all: check only the values currently visible under the search box, leaving the checked
  // state of non-matching values untouched. With no search, this selects everything (unfiltered).
  const selectAllFilter = (key: string) =>
    setFilters((prev) => {
      const shown = availableFilterValues;
      const current = prev[key] ? new Set(prev[key]) : new Set(shown);
      for (const v of visibleFilterValues) current.add(v);
      const next = { ...prev };
      if (current.size >= shown.length) delete next[key]; // everything in view selected → unfiltered
      else next[key] = current;
      return next;
    });

  // Clear: uncheck only the values currently visible under the search box, leaving other selected
  // values intact. With no search, this clears the whole column.
  const clearFilterValues = (key: string) =>
    setFilters((prev) => {
      const shown = availableFilterValues;
      const current = prev[key] ? new Set(prev[key]) : new Set(shown);
      for (const v of visibleFilterValues) current.delete(v);
      const next = { ...prev };
      if (current.size >= shown.length) delete next[key];
      else next[key] = current;
      return next;
    });

  // Clears EVERY active control, not just the column filters — the boxes and date ranges above the
  // table are filters too, and leaving them set after "Clear filters" hid rows with no visible
  // reason why.
  const clearAllFilters = () => {
    setFilters({});
    setBoughtFromMode('all');
    setMatchFind('');
    setQuickFind('');
    setDateRanges({ eventFrom: '', eventTo: '', orderFrom: '', orderTo: '' });
    // Back to the CURRENT season only — clearing must never surface last season's events.
    setSeasons(defaultSeasons());
  };

  // Copy the currently-filtered orders as a readable, grouped block:
  //   Match name (once)
  //     Platform (once per platform for that match)
  //       orderId | category | xQty | £totalGBP | buyer name | phone | email  (buyer fields if present)
  const copyOrders = async () => {
    if (visibleOrders.length === 0) {
      setToast({ message: 'No orders to copy', color: 'warning' });
      return;
    }
    // Group: match → platform → orders (keep each match's soonest date for sorting).
    const byMatch = new Map<string, { date: number; platforms: Map<string, Order[]> }>();
    for (const o of visibleOrders) {
      const match = String((o as any).eventName ?? '').trim() || '(no match)';
      const platform = toPlatformLabel(String(o.platform ?? '').trim());
      const t = (o as any).eventDate ? new Date((o as any).eventDate).getTime() : Infinity;
      if (!byMatch.has(match)) byMatch.set(match, { date: t, platforms: new Map() });
      const m = byMatch.get(match)!;
      if (t < m.date) m.date = t;
      if (!m.platforms.has(platform)) m.platforms.set(platform, []);
      m.platforms.get(platform)!.push(o);
    }

    const blocks: string[] = [];
    for (const [match, { platforms }] of [...byMatch.entries()].sort((a, b) => a[1].date - b[1].date)) {
      const lines: string[] = [match];
      for (const [platform, list] of [...platforms.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
        lines.push('', platform);
        for (const o of list) {
          const parts = [
            String(o.orderId ?? '').trim(),
            String((o as any).category ?? '').trim(),
            `x${Number(o.quantity) || 0}`,
            `£${formatMoney(computeTotalGBP(o))}`,
          ];
          const name = String((o as any).buyerName ?? '').trim();
          if (name) parts.push(name);
          const contact = String((o as any).buyerPhone ?? '').trim();
          if (contact) parts.push(contact.includes('@') ? `Email: ${contact}` : `Phone: ${contact}`);
          // Supplier last, and always present: "not bought yet" is as useful in a copied list as
          // the name is, so an unsourced sale says so rather than going silently blank.
          const supplier = String((o as any).boughtFrom ?? '').trim();
          const status = sourcingStatus(o);
          parts.push(
            status === 'none'
              ? 'NOT BOUGHT'
              : status === 'partial'
              ? `from: ${supplier || '?'} (${ticketsSourced(o)}/${Number(o.quantity) || 0} — still need ${ticketsOutstanding(o)})`
              : `from: ${supplier || '?'}`,
          );
          lines.push(parts.filter(Boolean).join(' | '));
        }
      }
      blocks.push(lines.join('\n'));
    }

    try {
      await navigator.clipboard.writeText(blocks.join('\n\n\n'));
      setToast({ message: `Copied ${visibleOrders.length} orders`, color: 'success' });
    } catch {
      setToast({ message: 'Clipboard copy failed', color: 'danger' });
    }
  };

  // Copy one order's details for the supplier: order number, buyer full name, buyer phone — one
  // per line, nothing else, so it pastes straight into a supplier chat.
  const copyForSupplier = async (order: any) => {
    const lines = [String(order.orderId ?? '').trim(), String(order.buyerName ?? '').trim(), String(order.buyerPhone ?? '').trim()];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setToast({ message: `Copied order ${lines[0]} for the supplier`, color: 'success' });
    } catch {
      setToast({ message: 'Clipboard copy failed', color: 'danger' });
    }
  };

  // Copy the order IDs of the currently-filtered (visible) rows to the clipboard, one per line.
  const copyOrderIds = async () => {
    const ids = visibleOrders.map((o) => String(o.orderId ?? '').trim()).filter(Boolean);
    if (ids.length === 0) {
      setToast({ message: 'No orders to copy', color: 'warning' });
      return;
    }
    try {
      await navigator.clipboard.writeText(ids.join('\n'));
      setToast({ message: `Copied ${ids.length} order ID${ids.length === 1 ? '' : 's'}`, color: 'success' });
    } catch {
      setToast({ message: 'Clipboard copy failed', color: 'danger' });
    }
  };

  // Quick-filter toggle for the top chips. Treat filters[key] as the explicit allow-set: unset = show
  // all, so the first click ISOLATES to `value`; further clicks add/remove. Empty (or all `allCount`
  // values selected) collapses back to unfiltered. Games pass allCount=Infinity (never auto-collapse).
  const quickToggle = (key: string, value: string, allCount: number) =>
    setFilters((prev) => {
      const current = new Set(prev[key] || []);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      const next = { ...prev };
      if (current.size === 0 || current.size >= allCount) delete next[key];
      else next[key] = current;
      return next;
    });

  const isQuickActive = (key: string, value: string) => !!filters[key] && filters[key].has(value);

  // A game chip stands for every date its fixture arrived under (see closestGames), so it toggles
  // all of them together — otherwise a click would filter in only the platforms that agreed on one
  // date and silently drop the rest of that game's sales.
  const isGameActive = (keys: string[]) => !!filters.gameKey && keys.some((k) => filters.gameKey.has(k));

  const quickToggleGame = (keys: string[]) =>
    setFilters((prev) => {
      const current = new Set(prev.gameKey || []);
      const on = keys.some((k) => current.has(k));
      for (const k of keys) {
        if (on) current.delete(k);
        else current.add(k);
      }
      const next = { ...prev };
      if (current.size === 0) delete next.gameKey;
      else next.gameKey = current;
      return next;
    });

  const chipClass = (active: boolean) =>
    'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ' +
    (active
      ? 'bg-blue-600 border-blue-600 text-white'
      : 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700');

  // Seating arrangement for the phone card, shortened: "Up To 2 Together" / "Up to 2 seats together"
// → "Up to 2", "Single Seat(s)" / "Singles" → "Singles". Anything else is shown as stored.
const seatingShort = (remarks: unknown): string => {
  const r = String(remarks ?? '').trim();
  if (!r) return '';
  if (/single/i.test(r)) return 'Singles';
  const m = r.match(/up\s*to\s*(\d+)/i);
  if (m) return `Up to ${m[1]}`;
  return r.length > 24 ? r.slice(0, 24) + '…' : r;
};

const shortDate = (ms: number) =>
    new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

  const openFilterMenu = (key: string, event: Event) => {
    setFilterSearch('');
    setFilterMenu({ key, event });
  };

  // Counts everything clearAllFilters resets, so the button appears whenever anything is filtering
  // — including when only the match box or a date range is set.
  const activeFilterCount =
    Object.keys(filters).length +
    (boughtFromMode !== 'all' ? 1 : 0) +
    (quickFind.trim() ? 1 : 0) +
    (matchFind.trim() ? 1 : 0) +
    dateRangeCount +
    (seasons.size === 1 && seasons.has(CURRENT_SEASON) ? 0 : 1);

  const [sortState, setSortState] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  const handleSortClick = (key: string) => {
    setSortState((prev) => {
      if (prev?.key === key) {
        return prev.dir === 'asc' ? { key, dir: 'desc' } : null;
      }
      return { key, dir: 'asc' };
    });
  };

  const sortedVisibleOrders = useMemo(() => {
    if (!sortState) return visibleOrders;
    const { key, dir } = sortState;
    const mul = dir === 'asc' ? 1 : -1;
    return [...visibleOrders].sort((a, b) => {
      let av: any, bv: any;
      if (key === 'profit') {
        av = hasPriceBought(a) ? computeProfit(a) : null;
        bv = hasPriceBought(b) ? computeProfit(b) : null;
      } else {
        av = (a as any)[key];
        bv = (b as any)[key];
      }
      // nulls last
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const col = COLUMNS.find((c) => c.key === key);
      const type = col?.type ?? 'text';
      if (type === 'date') return mul * (new Date(av).getTime() - new Date(bv).getTime());
      if (type === 'number') return mul * (Number(av) - Number(bv));
      const na = Number(av), nb = Number(bv);
      if (!isNaN(na) && !isNaN(nb)) return mul * (na - nb);
      return mul * String(av).localeCompare(String(bv));
    });
  }, [visibleOrders, sortState]);

  const columnSummary = useMemo(() => {
    if (!summaryMenu) return null;
    const { key } = summaryMenu;
    const col = COLUMNS.find((c) => c.key === key);
    const isNumber = key === 'profit' || col?.type === 'number';
    let count = 0;
    let sum = 0;
    for (const o of sortedVisibleOrders) {
      let val: number | null = null;
      if (key === 'profit') {
        val = hasPriceBought(o) ? computeProfit(o) : null;
      } else if (key === 'pricePerTicket') {
        val = computePricePerTicket(o);
      } else {
        const raw = (o as any)[key];
        val = raw != null && raw !== '' ? Number(raw) : null;
      }
      if (isNumber) {
        if (val != null && !isNaN(val)) { count++; sum += val; }
      } else {
        const raw = (o as any)[key];
        if (raw != null && raw !== '') count++;
      }
    }
    return { isNumber, count, sum, avg: count > 0 ? sum / count : 0 };
  }, [summaryMenu, sortedVisibleOrders]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: sortedVisibleOrders.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 37,
    overscan: 10,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const totalVirtualSize = virtualizer.getTotalSize();
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom = totalVirtualSize - (virtualRows[virtualRows.length - 1]?.end ?? 0);

  // ── Mobile (phone) view ──────────────────────────────────────────────────────
  // Below `sm` the 22-column table is hopeless (~3,000px wide with 330px of frozen columns), so
  // phones get a virtualized CARD list over the same filtered rows, and editing moves into a
  // bottom sheet (tap a card) that exposes every column as a labelled field.
  const isMobile = useIsMobile();
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const mobileVirtualizer = useVirtualizer({
    count: sortedVisibleOrders.length,
    getScrollElement: () => mobileScrollRef.current,
    estimateSize: () => 140,
    overscan: 8,
  });
  const mobileRows = mobileVirtualizer.getVirtualItems();

  // The order open in the edit sheet, plus its draft values (keyed by column key, display format —
  // same conventions as the table cells: dates yyyy-MM-dd, platform relabelled).
  const [sheetOrderId, setSheetOrderId] = useState<string | null>(null);
  const [sheetValues, setSheetValues] = useState<Record<string, string>>({});
  const sheetOrder = useMemo(
    () => (sheetOrderId ? orders.find((o) => rowId(o) === sheetOrderId) || null : null),
    [orders, sheetOrderId],
  );

  const displayValueFor = (order: Order, col: ColumnDef): string => {
    const raw = order[col.key];
    if (col.type === 'date') return toDateInput(raw as string | Date);
    if (col.type === 'number')
      return raw != null && raw !== '' ? String(Math.round(Number(raw) * 100) / 100) : '';
    if (raw == null) return '';
    if (col.key === 'platform') return toPlatformLabel(String(raw));
    return String(raw);
  };

  const openSheet = (order: Order) => {
    const values: Record<string, string> = {};
    for (const col of COLUMNS) values[col.key as string] = displayValueFor(order, col);
    setSheetValues(values);
    setSheetOrderId(rowId(order));
  };

  // Persist every field the user actually changed (same per-field persist the table cells use, so
  // profit recomputation and socket broadcasts behave identically).
  const saveSheet = async () => {
    if (!sheetOrder) return;
    const id = rowId(sheetOrder);
    for (const col of COLUMNS) {
      const key = col.key as string;
      const display = sheetValues[key] ?? '';
      if (display === displayValueFor(sheetOrder, col)) continue;
      const val = col.key === 'platform' ? toPlatformStore(display) : display;
      updateLocal(id, col.key, val);
      await persist(id, col.key, val, col.type);
    }
    setSheetOrderId(null);
  };

  // Footer totals across the currently visible (filtered) rows.
  const totals = visibleOrders.reduce<{ total: number; gbp: number; bought: number; profit: number; qty: number; needed: number }>(
    (acc, o) => {
      acc.total += Number(o.totalPriceGBP) || 0;
      acc.gbp += computeTotalGBP(o);
      acc.bought += Number(o.priceBought) || 0;
      acc.profit += hasPriceBought(o) ? computeProfitGBP(o) : 0;
      acc.qty += Number(o.quantity) || 0;
      // Only PART-bought sales count here — an untouched order's whole quantity isn't "outstanding"
      // in the same sense, and lumping them together would drown the signal.
      if (sourcingStatus(o) === 'partial') acc.needed += ticketsOutstanding(o);
      return acc;
    },
    { total: 0, gbp: 0, bought: 0, profit: 0, qty: 0, needed: 0 }
  );
  const totalPricePerTicket = totals.qty > 0 ? totals.total / totals.qty : totals.total;

  // Spreadsheet-style keyboard navigation: focus the input at grid position (row, col). If the row
  // isn't currently rendered (virtualized away), scroll it into view first, then focus on the next
  // frames. `col` indexes into COLUMNS (the editable data columns).
  const focusCell = (r: number, c: number) => {
    if (r < 0 || r > sortedVisibleOrders.length - 1 || c < 0 || c > VISIBLE_COLUMNS.length - 1) return;
    const root = scrollRef.current;
    if (!root) return;
    const grab = () => {
      const el = root.querySelector<HTMLInputElement>(`input[data-r="${r}"][data-c="${c}"]`);
      if (!el) return false;
      el.focus();
      try { el.select(); } catch { /* date inputs don't support select() */ }
      return true;
    };
    if (!grab()) {
      virtualizer.scrollToIndex(r, { align: 'auto' });
      requestAnimationFrame(() => requestAnimationFrame(grab));
    }
  };

  const cellClass =
    'w-full bg-transparent px-2 py-1.5 text-sm border border-transparent rounded ' +
    'focus:outline-none focus:border-blue-400 focus:bg-blue-50 dark:focus:bg-blue-950/30 focus:ring-0 ' +
    'text-gray-800 dark:text-gray-100 placeholder-gray-400';

  // Sticky-footer cell base: pins the totals row to the bottom of the scroll container (mirrors the
  // header's `sticky top-0`). Each cell needs its own opaque bg + top border so the row paints over
  // scrolling content.
  const footerCell =
    'sticky bottom-0 z-10 bg-slate-100 dark:bg-slate-800 ' +
    'border-t-2 border-slate-300 dark:border-slate-600 border-r border-r-slate-200 dark:border-r-slate-700';

  // Profit column header — computed column, inserted in the flatMap right after "Price Bought".
  const waHeaderTh = (
    <th
      key="waStatus"
      style={{ minWidth: '150px' }}
      className="sticky top-0 z-10 px-2 py-2.5 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide border-b-2 border-slate-200 dark:border-slate-700 border-r border-r-slate-200 dark:border-r-slate-700 whitespace-nowrap bg-slate-100 dark:bg-slate-800"
    >
      <div className="flex items-center justify-between gap-1">
        <span>WA Status</span>
        <button
          type="button"
          title="Filter"
          onClick={(e) => openFilterMenu('waStatus', e.nativeEvent)}
          className={
            isColumnFiltered('waStatus')
              ? 'text-blue-500 dark:text-blue-400'
              : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
          }
        >
          <IonIcon icon={isColumnFiltered('waStatus') ? funnel : funnelOutline} />
        </button>
      </div>
    </th>
  );

  // Where the order sits in the delivery pipeline — coarser than WA Status, which tracks receipts.
  const deliveryHeaderTh = (
    <th
      key="delivery"
      style={{ minWidth: '130px' }}
      className="sticky top-0 z-10 px-2 py-2.5 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide border-b-2 border-slate-200 dark:border-slate-700 border-r border-r-slate-200 dark:border-r-slate-700 whitespace-nowrap bg-slate-100 dark:bg-slate-800"
    >
      <div className="flex items-center justify-between gap-1">
        <span>Delivery</span>
        <button
          type="button"
          title="Filter"
          onClick={(e) => openFilterMenu('delivery', e.nativeEvent)}
          className={
            isColumnFiltered('delivery')
              ? 'text-blue-500 dark:text-blue-400'
              : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
          }
        >
          <IonIcon icon={isColumnFiltered('delivery') ? funnel : funnelOutline} />
        </button>
      </div>
    </th>
  );

  // Click on a "Processed" chip = mark delivered by hand (no proof, nothing sent); click on a
  // "Delivered (manual)" chip = undo. Real deliveries (receipts, supplier proof) are not clickable
  // — they were earned, not asserted.
  const toggleManualDelivered = async (order: Order) => {
    const id = String((order as any).orderId ?? '');
    const isManual = String((order as any).deliveryDetail ?? '') === 'Delivered (manual)';
    const msg = isManual
      ? `Un-mark order ${id} as delivered?`
      : `Mark order ${id} as delivered — manual, no proof, nothing sent to the buyer?`;
    if (!window.confirm(msg)) return;
    try {
      await markOrderDelivered(id, !isManual);
      setOrders((prev) =>
        prev.map((o) =>
          String((o as any).orderId) === id
            ? ({
                ...o,
                delivery: isManual ? 'Not delivered' : 'Delivered',
                deliveryDetail: isManual ? 'Processed' : 'Delivered (manual)',
                deliveryStage: isManual ? 'processed' : 'delivered',
                deliveryWarning: false,
              } as Order)
            : o,
        ),
      );
    } catch {
      setToast({ message: 'Failed to update delivery mark', color: 'danger' });
    }
  };

  // Green = tickets gone, amber = held on the buyer, slate = nothing sent yet.
  const deliveryChipClass = (label: string): string =>
    /FAILED/.test(label)
      ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
      : /^Delivered · sent/.test(label)
      ? // reached WhatsApp but not the handset — green would overstate it
        'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300'
      : /^Delivered/.test(label)
      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
      : /Awaiting/.test(label)
      ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300';

  // Chip colouring by stage: red = failed, amber = gate armed / unseen, green = seen/done.
  const waChipClass = (label: string): string => {
    if (!label || label === 'message not sent' || label === 'no phone') return 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400';
    if (/FAILED/i.test(label)) return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
    if (/not approved/.test(label)) return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300';
    if (/tickets seen/.test(label)) return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300';
    if (/tickets/.test(label)) return 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300';
    if (/seen/.test(label)) return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400';
    return 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300';
  };

  const profitHeaderTh = (
    <th
      key="profit"
      style={{ minWidth: '110px' }}
      className="sticky top-0 z-10 px-2 py-2.5 text-right text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide border-b-2 border-slate-200 dark:border-slate-700 border-r border-r-slate-200 dark:border-r-slate-700 whitespace-nowrap bg-slate-100 dark:bg-slate-800"
    >
      <div className="flex items-center justify-between gap-1">
        <button
          type="button"
          className="text-left font-semibold hover:text-blue-600 dark:hover:text-blue-400 transition-colors focus:outline-none"
          onClick={(e) => setSummaryMenu({ key: 'profit', event: e.nativeEvent })}
        >
          Profit
        </button>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            title={
              sortState?.key === 'profit'
                ? sortState.dir === 'asc'
                  ? 'Sorted A→Z (click for Z→A)'
                  : 'Sorted Z→A (click to clear)'
                : 'Sort'
            }
            onClick={() => handleSortClick('profit')}
            className={
              sortState?.key === 'profit'
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
            }
          >
            <IonIcon
              icon={
                sortState?.key === 'profit'
                  ? sortState.dir === 'asc'
                    ? arrowUpOutline
                    : arrowDownOutline
                  : swapVerticalOutline
              }
            />
          </button>
          <button
            type="button"
            title="Filter"
            onClick={(e) => openFilterMenu('profit', e.nativeEvent)}
            className={
              isColumnFiltered('profit')
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
            }
          >
            <IonIcon icon={isColumnFiltered('profit') ? funnel : funnelOutline} />
          </button>
        </div>
      </div>
    </th>
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Orders</IonTitle>
          <IonButtons slot="end">
            {saving && <IonSpinner name="dots" className="mr-2" />}
            {/* Labels hide below sm so four buttons fit an iPhone toolbar as icons */}
            {activeFilterCount > 0 && (
              <IonButton onClick={clearAllFilters} title="Clear all filters" aria-label="Clear all filters">
                <IonIcon slot="start" icon={funnel} />
                <span className="hidden sm:inline">Clear filters ({activeFilterCount})</span>
              </IonButton>
            )}
            <IonButton
              onClick={refreshStatuses}
              disabled={statusRefreshing}
              title="Re-check delivery status for the orders currently shown — keeps your filters"
              aria-label="Refresh delivery status"
            >
              {statusRefreshing ? <IonSpinner name="dots" /> : <IonIcon slot="start" icon={checkmarkDoneOutline} />}
              <span className="hidden sm:inline">Refresh status</span>
            </IonButton>
            <IonButton onClick={copyOrders} title="Copy filtered orders (grouped by match & platform)" aria-label="Copy orders">
              <IonIcon slot="start" icon={clipboardOutline} />
              <span className="hidden sm:inline">Copy Orders ({visibleOrders.length})</span>
            </IonButton>
            <IonButton onClick={loadOrders} title="Reload everything from the server (clears unsaved edits)">
              <IonIcon slot="icon-only" icon={refreshOutline} />
            </IonButton>
            <IonButton
              onClick={() => setPrivatePick(true)}
              disabled={saving}
              title="Add a private sale — sold directly to a supplier or private client, not through a platform"
              aria-label="Add private sale"
            >
              <IonIcon slot="start" icon={personAddOutline} />
              <span className="hidden sm:inline">Add Private</span>
            </IonButton>
            <IonButton onClick={() => addOrder()} disabled={saving} strong title="Add order" aria-label="Add order">
              <IonIcon slot="start" icon={addOutline} />
              <span className="hidden sm:inline">Add Order</span>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={false}>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <IonSpinner />
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {/* Quick-filter chips: platforms + the 10 nearest games. Multi-select; they drive the same
                `filters` state as the column menus, so they compose with everything else. */}
            <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-950 px-3 py-2 space-y-1.5">
              <div className="flex flex-wrap items-center gap-1.5">
                {/* Quick order lookup — overrides all other filters while it has text */}
                <div className="relative shrink-0">
                  <input
                    value={quickFind}
                    onChange={(e) => setQuickFind(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setQuickFind('');
                    }}
                    placeholder="Find order id…"
                    className={
                      'w-40 pl-2 pr-6 py-1 rounded-md border text-sm bg-white dark:bg-gray-900 ' +
                      (quickFind.trim()
                        ? 'border-blue-500 ring-1 ring-blue-500'
                        : 'border-slate-300 dark:border-slate-600')
                    }
                  />
                  {quickFind && (
                    <button
                      onClick={() => setQuickFind('')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-sm px-0.5"
                      title="Clear (Esc)"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {/* Match-name quick filter — composes with chips/filters (see visibleOrders) */}
                <div className="relative shrink-0">
                  <input
                    value={matchFind}
                    onChange={(e) => setMatchFind(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setMatchFind('');
                    }}
                    placeholder="Find match…"
                    className={
                      'w-40 pl-2 pr-6 py-1 rounded-md border text-sm bg-white dark:bg-gray-900 ' +
                      (matchFind.trim()
                        ? 'border-blue-500 ring-1 ring-blue-500'
                        : 'border-slate-300 dark:border-slate-600')
                    }
                  />
                  {matchFind && (
                    <button
                      onClick={() => setMatchFind('')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-sm px-0.5"
                      title="Clear (Esc)"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {/* Date-range filter toggle — the ranges themselves live in the row below */}
                <button
                  type="button"
                  onClick={() => setDatesOpen((v) => !v)}
                  className={'inline-flex items-center gap-1 shrink-0 ' + chipClass(dateRangeCount > 0)}
                  title="Filter by event date or order date range"
                >
                  <IonIcon icon={calendarOutline} />
                  Dates{dateRangeCount > 0 ? ` (${dateRangeCount})` : ''}
                </button>
                {quickFind.trim() && (
                  <span className="text-xs text-blue-600 dark:text-blue-400 shrink-0">
                    {visibleOrders.length} match{visibleOrders.length === 1 ? '' : 'es'} — other filters paused
                  </span>
                )}
                {/* Phone-only: the column filters live in the table headers on desktop, which the
                    card view doesn't have — this opens them as a bottom sheet instead. */}
                <button
                  type="button"
                  onClick={() => {
                    setFilterSheetKey(null);
                    setFilterSearch('');
                    setFilterSheetOpen(true);
                  }}
                  className={'sm:hidden inline-flex items-center gap-1 ' + chipClass(activeFilterCount > 0)}
                >
                  <IonIcon icon={activeFilterCount > 0 ? funnel : funnelOutline} />
                  Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </button>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 shrink-0">Season</span>
                {SEASONS.map((sn) => {
                  const on = seasons.has(sn.id);
                  return (
                    <button
                      key={sn.id}
                      type="button"
                      title={`${sn.id} season${sn.id === CURRENT_SEASON ? ' (shown by default)' : ' — last season, hidden unless ticked'}`}
                      onClick={() =>
                        setSeasons((prev) => {
                          const next = new Set(prev);
                          if (next.has(sn.id)) next.delete(sn.id);
                          else next.add(sn.id);
                          // Never leave nothing selected — that would empty the table with no
                          // obvious cause. Falling back to the current season is the sane floor.
                          return next.size ? next : defaultSeasons();
                        })
                      }
                      className={chipClass(on)}
                    >
                      {sn.label}
                    </button>
                  );
                })}
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 shrink-0 ml-1">Platform</span>
                {platformChips.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => quickToggle('platform', p, platformChips.length)}
                    className={chipClass(isQuickActive('platform', p))}
                  >
                    {p}
                  </button>
                ))}
                {/* Sourcing toggle — composes with all other filters */}
                <div className="flex items-center ml-auto shrink-0">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mr-1">Sourced</span>
                  {(['all', 'empty', 'partial', 'filled'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      title={
                        m === 'all'
                          ? 'Show all orders'
                          : m === 'empty'
                          ? 'Orders still needing tickets — nothing bought yet, PLUS part-bought ones'
                          : m === 'partial'
                          ? 'Only part-bought orders (some tickets secured, some still missing)'
                          : 'Only fully bought orders'
                      }
                      onClick={() => setBoughtFromMode(m)}
                      className={
                        'px-2 py-0.5 text-xs font-medium border border-slate-300 dark:border-slate-600 transition-colors ' +
                        (m === 'all' ? 'rounded-l-md ' : '') +
                        (m === 'filled' ? 'rounded-r-md ' : '') +
                        (m !== 'all' ? '-ml-px ' : '') +
                        (boughtFromMode === m
                          ? 'relative z-10 bg-blue-600 border-blue-600 text-white'
                          : m === 'partial' && partialCount > 0
                          ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/60'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700')
                      }
                    >
                      {m === 'all'
                        ? 'All'
                        : m === 'empty'
                        ? 'Needed'
                        : m === 'partial'
                        ? `Partial${partialCount ? ` (${partialCount})` : ''}`
                        : 'Bought'}
                    </button>
                  ))}
                </div>
              </div>
              {/* Date-range row — opened by the Dates chip. Native date inputs; clearing a box
                  reopens that bound. Wraps on phones so both ranges stay reachable. */}
              {datesOpen && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {(
                    [
                      { label: 'Event date', fromKey: 'eventFrom', toKey: 'eventTo' },
                      { label: 'Order date', fromKey: 'orderFrom', toKey: 'orderTo' },
                    ] as const
                  ).map(({ label, fromKey, toKey }) => (
                    <div key={label} className="flex items-center gap-1 shrink-0">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
                      {([fromKey, toKey] as const).map((k, i) => (
                        <React.Fragment key={k}>
                          {i === 1 && <span className="text-xs text-slate-400">–</span>}
                          <input
                            type="date"
                            value={dateRanges[k]}
                            onChange={(e) => setDateRanges((prev) => ({ ...prev, [k]: e.target.value }))}
                            className={
                              'px-1.5 py-0.5 rounded-md border text-xs bg-white dark:bg-gray-900 ' +
                              (dateRanges[k]
                                ? 'border-blue-500 ring-1 ring-blue-500'
                                : 'border-slate-300 dark:border-slate-600')
                            }
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  ))}
                  {dateRangeCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setDateRanges({ eventFrom: '', eventTo: '', orderFrom: '', orderTo: '' })}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline shrink-0"
                    >
                      Clear dates
                    </button>
                  )}
                  {eventMonths.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap ml-auto">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 shrink-0">Month</span>
                      {eventMonths.map((ym) => (
                        <button
                          key={ym}
                          type="button"
                          title={`Only orders whose event is in ${monthLabel(ym, true)}`}
                          onClick={() => {
                            const r = monthRange(ym);
                            setDateRanges((prev) =>
                              activeMonth === ym
                                ? { ...prev, eventFrom: '', eventTo: '' }
                                : { ...prev, eventFrom: r.from, eventTo: r.to },
                            );
                          }}
                          className={
                            'px-1.5 py-0.5 rounded text-[11px] font-medium border transition-colors whitespace-nowrap ' +
                            (activeMonth === ym
                              ? 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700')
                          }
                        >
                          {monthLabel(ym)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* On phones this row scrolls sideways instead of wrapping — 20 game chips stacked
                  vertically would push the list itself below the fold. */}
              <div className="flex sm:flex-wrap items-center gap-1.5 overflow-x-auto sm:overflow-visible">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 w-16 shrink-0">Games</span>
                {closestGames.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    title={`${g.name} — ${new Date(g.date).toLocaleDateString()}`}
                    onClick={() => quickToggleGame(g.keys)}
                    className={chipClass(isGameActive(g.keys)) + ' inline-flex items-center gap-1 max-w-[240px] shrink-0'}
                  >
                    <span className="truncate min-w-0">{g.name}</span>
                    <span className="opacity-60 shrink-0">{shortDate(g.date)}</span>
                  </button>
                ))}
                {closestGames.length === 0 && (
                  <span className="text-xs text-slate-400">No dated games</span>
                )}
                <button
                  type="button"
                  onClick={openRejected}
                  title="Orders rejected on HelloTickets — kept as a record, hidden from this table"
                  className="ml-auto shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                >
                  Rejected
                </button>
              </div>
            </div>
            {isMobile ? (
              /* ── Phone: virtualized card list over the same filtered rows ── */
              <>
                <div ref={mobileScrollRef} className="overflow-y-auto flex-1 bg-white dark:bg-gray-950">
                  {sortedVisibleOrders.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                      {orders.length === 0
                        ? 'No orders yet. Tap + to create your first order.'
                        : 'No orders match the current filters.'}
                    </div>
                  ) : (
                    <div className="relative" style={{ height: mobileVirtualizer.getTotalSize() }}>
                      {mobileRows.map((vr) => {
                        const order = sortedVisibleOrders[vr.index];
                        const id = rowId(order);
                        const showProfit = hasPriceBought(order);
                        const profit = computeProfit(order);
                        const boughtFrom = String((order as any).boughtFrom ?? '').trim();
                        const status = sourcingStatus(order);
                        const buyer = String((order as any).buyerName ?? '').trim();
                        const phone = String((order as any).buyerPhone ?? '').trim();
                        const cur = String((order as any).currency || 'GBP');
                        return (
                          <div
                            key={id}
                            data-index={vr.index}
                            ref={mobileVirtualizer.measureElement}
                            className="absolute left-0 right-0"
                            style={{ transform: `translateY(${vr.start}px)` }}
                          >
                            <button
                              type="button"
                              onClick={() => openSheet(order)}
                              className={
                                'w-full text-left px-3 py-2.5 border-b border-slate-200 dark:border-slate-800 active:bg-blue-50 dark:active:bg-blue-950/30' +
                                (status === 'partial'
                                  ? ' bg-amber-50 dark:bg-amber-950/40 border-l-4 border-l-amber-400'
                                  : '')
                              }
                            >
                              <div className="flex items-baseline gap-2">
                                <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                                  {String(order.orderId ?? '') || '(no id)'}
                                </span>
                                {order.platform && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                    {toPlatformLabel(String(order.platform))}
                                  </span>
                                )}
                                {String((order as any).waStatus ?? '') && (
                                  <span className={'text-[10px] px-1.5 py-0.5 rounded-full ' + waChipClass(String((order as any).waStatus))}>
                                    {String((order as any).waStatus)}
                                  </span>
                                )}
                                {(order as any).resold && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-semibold">
                                    {(order as any).resold.partial ? 'Part-resold' : 'Resold'}
                                  </span>
                                )}
                                {String((order as any).deliveryDetail ?? '') && (
                                  <span className={'text-[10px] px-1.5 py-0.5 rounded-full ' + deliveryChipClass(String((order as any).deliveryDetail))}>
                                    {String((order as any).deliveryDetail)}
                                    {(order as any).deliveryWarning ? ' ⚠' : ''}
                                  </span>
                                )}
                                <span className="ml-auto font-semibold text-sm text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                                  {formatMoneyCur(computeTotalGBP(order), 'GBP')}
                                </span>
                              </div>
                              {order.eventName && (
                                <div className="mt-0.5 text-sm font-medium text-gray-800 dark:text-gray-200 break-words">
                                  {String(order.eventName)}
                                </div>
                              )}
                              <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                                {(order as any).eventDate && (
                                  <span>{shortDate(new Date((order as any).eventDate).getTime())}</span>
                                )}
                                <span>x{Number(order.quantity) || 0}</span>
                                {seatingShort((order as any).remarks) && (
                                  <span className="text-slate-700 dark:text-slate-200 font-medium">{seatingShort((order as any).remarks)}</span>
                                )}
                                {(order as any).category && <span>{String((order as any).category)}</span>}
                                {cur !== 'GBP' && (
                                  <span>
                                    {formatMoneyCur(Number(order.totalPriceGBP) || 0, cur)} {cur}
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs">
                                {boughtFrom ? (
                                  <span className="text-slate-600 dark:text-slate-300">
                                    From {boughtFrom}
                                    {hasPriceBought(order) &&
                                      ` · ${formatMoneyCur(Number(order.priceBought) || 0, 'GBP')}`}
                                  </span>
                                ) : status === 'none' ? (
                                  <span className="text-amber-600 dark:text-amber-400">No supplier</span>
                                ) : null}
                                {status === 'partial' && (
                                  <span className="px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 font-semibold">
                                    {ticketsSourced(order)}/{Number(order.quantity) || 0} — need{' '}
                                    {ticketsOutstanding(order)} more
                                  </span>
                                )}
                                {showProfit && (
                                  <span
                                    className={
                                      'ml-auto font-semibold ' +
                                      (profit >= 0
                                        ? 'text-emerald-600 dark:text-emerald-400'
                                        : 'text-red-500 dark:text-red-400')
                                    }
                                  >
                                    {formatProfitCell(order)}
                                  </span>
                                )}
                              </div>
                              {(buyer || phone) && (
                                <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {[buyer, phone].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Sticky totals bar — same numbers as the desktop footer */}
                {sortedVisibleOrders.length > 0 && (
                  <div className="shrink-0 border-t-2 border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-3 py-2 flex items-center justify-between text-xs">
                    <span className="text-slate-500 dark:text-slate-400">
                      {visibleOrders.length} orders · x{totals.qty}
                      {totals.needed > 0 && (
                        <span className="text-amber-600 dark:text-amber-400 font-semibold">
                          {' '}· need {totals.needed}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                        {formatMoneyCur(totals.gbp, 'GBP')}
                      </span>
                      <span
                        className={
                          'font-semibold ' +
                          (totals.profit >= 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-red-500 dark:text-red-400')
                        }
                      >
                        {formatMoneyCur(totals.profit, 'GBP')}
                      </span>
                    </span>
                  </div>
                )}
              </>
            ) : (
            <div ref={scrollRef} className="overflow-auto flex-1 bg-white dark:bg-gray-950">
              <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-100 dark:bg-slate-800">
                  {VISIBLE_COLUMNS.flatMap((col) => {
                    const key = col.key as string;
                    const expanded = !!expandedCols[key];
                    const th = (
                      <th
                        key={key}
                        style={colStyle(col)}
                        className={
                          'sticky top-0 px-2 py-2.5 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide border-b-2 border-slate-200 dark:border-slate-700 border-r border-r-slate-200 dark:border-r-slate-700 whitespace-nowrap bg-slate-100 dark:bg-slate-800 ' +
                          // Order Id + Event Name stay pinned during horizontal scroll (corner cells
                          // need a higher z so both the other headers and the body slide under them).
                          (key === 'orderId'
                            ? 'left-0 z-30'
                            : key === 'eventName'
                              ? 'left-[130px] z-30'
                              : 'z-10')
                        }
                      >
                        <div className="flex items-center justify-between gap-1">
                          <button
                            type="button"
                            className="text-left font-semibold hover:text-blue-600 dark:hover:text-blue-400 transition-colors focus:outline-none"
                            onClick={(e) => setSummaryMenu({ key, event: e.nativeEvent })}
                          >
                            {col.label}
                          </button>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {key === 'orderId' && (
                              <button
                                type="button"
                                title={`Copy ${visibleOrders.length} filtered order ID(s)`}
                                onClick={copyOrderIds}
                                className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
                              >
                                <IonIcon icon={copyOutline} />
                              </button>
                            )}
                            <button
                              type="button"
                              title={expanded ? 'Collapse column' : 'Expand column'}
                              onClick={() => toggleExpand(key)}
                              className={
                                expanded
                                  ? 'text-blue-500 dark:text-blue-400'
                                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                              }
                            >
                              <IonIcon icon={expanded ? contractOutline : expandOutline} />
                            </button>
                            <button
                              type="button"
                              title={
                                sortState?.key === key
                                  ? sortState.dir === 'asc'
                                    ? 'Sorted A→Z (click for Z→A)'
                                    : 'Sorted Z→A (click to clear)'
                                  : 'Sort'
                              }
                              onClick={() => handleSortClick(key)}
                              className={
                                sortState?.key === key
                                  ? 'text-blue-500 dark:text-blue-400'
                                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                              }
                            >
                              <IonIcon
                                icon={
                                  sortState?.key === key
                                    ? sortState.dir === 'asc'
                                      ? arrowUpOutline
                                      : arrowDownOutline
                                    : swapVerticalOutline
                                }
                              />
                            </button>
                            <button
                              type="button"
                              title="Filter"
                              onClick={(e) => openFilterMenu(key, e.nativeEvent)}
                              className={
                                isColumnFiltered(key)
                                  ? 'text-blue-500 dark:text-blue-400'
                                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                              }
                            >
                              <IonIcon icon={isColumnFiltered(key) ? funnel : funnelOutline} />
                            </button>
                          </div>
                        </div>
                      </th>
                    );
                    if (key === 'totalPriceGBP') {
                      return [(
                        <th
                          key="pricePerTicket"
                          style={{ minWidth: '120px' }}
                          className="sticky top-0 z-10 px-2 py-2 text-right font-semibold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 whitespace-nowrap bg-gray-100 dark:bg-gray-800"
                        >
                          <button
                            type="button"
                            className="font-semibold hover:underline focus:outline-none"
                            onClick={(e) => setSummaryMenu({ key: 'pricePerTicket', event: e.nativeEvent })}
                          >
                            Price/Ticket
                          </button>
                        </th>
                      ), th, (
                        <th
                          key="totalGbp"
                          style={{ minWidth: '140px' }}
                          className="sticky top-0 z-10 px-2 py-2 text-right text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 border-b-2 border-slate-200 dark:border-slate-700 border-r border-r-slate-200 dark:border-r-slate-700 whitespace-nowrap bg-slate-100 dark:bg-slate-800"
                        >
                          Total GBP Price
                        </th>
                      )];
                    }
                    if (key === 'priceBought') {
                      return [th, profitHeaderTh];
                    }
                    if (key === 'helloReference') {
                      return [th, waHeaderTh, deliveryHeaderTh];
                    }
                    return [th];
                  })}
                  <th className="sticky top-0 z-10 px-2 py-2.5 border-b-2 border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 w-10" />
                </tr>
              </thead>
              <tbody>
                {sortedVisibleOrders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={VISIBLE_COLUMNS.length + 5}
                      className="px-4 py-10 text-center text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
                    >
                      {orders.length === 0
                        ? 'No orders yet. Click "Add Order" to create your first row.'
                        : 'No orders match the current filters.'}
                    </td>
                  </tr>
                ) : (
                  <>
                    {paddingTop > 0 && (
                      <tr><td colSpan={VISIBLE_COLUMNS.length + 5} style={{ height: paddingTop }} /></tr>
                    )}
                    {virtualRows.map((virtualRow) => {
                      const order = sortedVisibleOrders[virtualRow.index];
                      const id = rowId(order);
                      const showProfit = hasPriceBought(order);
                      const profit = computeProfit(order);
                      // Part-bought sales are tinted amber across the whole row so they're
                      // impossible to scroll past — the sale isn't closed until the rest is bought.
                      const partial = sourcingStatus(order) === 'partial';
                      const rowBg = partial
                        ? 'bg-amber-50 dark:bg-amber-950/40'
                        : virtualRow.index % 2 === 0
                          ? 'bg-white dark:bg-gray-950'
                          : 'bg-slate-50 dark:bg-slate-900';
                      const pinned = pinnedRowId === id;
                      return (
                        <tr
                          key={id}
                          data-index={virtualRow.index}
                          ref={virtualizer.measureElement}
                          onClick={() => setPinnedRowId((prev) => (prev === id ? null : id))}
                          className={
                            (pinned
                              ? // pinned = the hover colour, frozen — scroll anywhere, it stays
                                'bg-sky-100 dark:bg-sky-900/50'
                              : partial
                              ? 'bg-amber-50 dark:bg-amber-950/40'
                              : virtualRow.index % 2 === 0
                                ? 'bg-white dark:bg-gray-950'
                                : 'bg-slate-50/60 dark:bg-slate-900/40') +
                            ' hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors'
                          }
                        >
                          {VISIBLE_COLUMNS.flatMap((col, colIdx) => {
                            const cellKey = `${id}_${col.key as string}`;
                            const raw = order[col.key];
                            const storedValue =
                              col.type === 'date'
                                ? toDateInput(raw as string | Date)
                                : col.type === 'number' && raw != null && raw !== ''
                                ? String(Math.round(Number(raw) * 100) / 100)
                                : raw == null
                                ? ''
                                : col.key === 'platform'
                                ? toPlatformLabel(String(raw))
                                : String(raw);
                            const inputValue = cellKey in editingCells ? editingCells[cellKey] : storedValue;
                            const lock = lockedByOther(id, col.key as string); // being edited by someone else
                            // Pinned columns need their own SOLID background (the zebra bg lives on
                            // the tr and would let scrolled content show through a transparent cell).
                            const stickyTd =
                              col.key === 'orderId'
                                ? ' sticky left-0 z-[5] ' + rowBg
                                : col.key === 'eventName'
                                  ? ' sticky left-[130px] z-[5] ' + rowBg
                                  : '';
                            const td = (
                              <td
                                key={col.key as string}
                                style={colStyle(col)}
                                className={'border-r border-b border-slate-200 dark:border-slate-800 p-0' + stickyTd}
                              >
                                <input
                                  type={col.type === 'date' ? 'date' : 'text'}
                                  inputMode={col.type === 'number' ? 'decimal' : undefined}
                                  data-r={virtualRow.index}
                                  data-c={colIdx}
                                  disabled={!!lock}
                                  title={lock ? `Editing by ${lock.by}` : undefined}
                                  className={
                                    cellClass +
                                    (lock
                                      ? ' !bg-amber-100 dark:!bg-amber-900/40 ring-1 ring-amber-400 cursor-not-allowed'
                                      : '')
                                  }
                                  style={{ textAlign: col.align || 'left' }}
                                  value={inputValue}
                                  onChange={(e) =>
                                    setEditingCells((prev) => ({ ...prev, [cellKey]: e.target.value }))
                                  }
                                  onFocus={() => {
                                    focusedCellRef.current = `${id}::${col.key as string}`;
                                    lockCell(id, col.key as string); // claim the cell for editing
                                  }}
                                  onBlur={(e) => {
                                    focusedCellRef.current = null;
                                    unlockCell(id, col.key as string); // release the cell
                                    const display = e.target.value;
                                    setEditingCells((prev) => {
                                      if (!(cellKey in prev)) return prev; // nothing edited → no re-render
                                      const next = { ...prev };
                                      delete next[cellKey];
                                      return next;
                                    });
                                    // Unchanged (e.g. just navigating through cells) → skip the save.
                                    if (display === storedValue) return;
                                    // Platform is shown relabelled (e.g. "Live"); map back to the
                                    // canonical stored value so the DB value never drifts.
                                    const val = col.key === 'platform' ? toPlatformStore(display) : display;
                                    updateLocal(id, col.key, val);
                                    persist(id, col.key, val, col.type);
                                  }}
                                  onKeyDown={(e) => {
                                    // Spreadsheet navigation. Focusing the neighbour blurs this cell,
                                    // so the current edit is committed via onBlur before moving.
                                    const t = e.target as HTMLInputElement;
                                    const r = virtualRow.index;
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      focusCell(r + 1, colIdx); // Enter → one cell down
                                      return;
                                    }
                                    if (col.type === 'date') return; // let date inputs use arrows natively
                                    if (e.key === 'ArrowDown') {
                                      e.preventDefault();
                                      focusCell(r + 1, colIdx);
                                    } else if (e.key === 'ArrowUp') {
                                      e.preventDefault();
                                      focusCell(r - 1, colIdx);
                                    } else if (e.key === 'ArrowLeft') {
                                      // Leave the cell when the selection/caret reaches the left edge.
                                      // A freshly-navigated cell is fully highlighted (start === 0), so
                                      // the first press moves rather than just clearing the selection;
                                      // mid-text the caret still moves within the value.
                                      if (t.selectionStart === 0) {
                                        e.preventDefault();
                                        focusCell(r, colIdx - 1);
                                      }
                                    } else if (e.key === 'ArrowRight') {
                                      if (t.selectionEnd === t.value.length) {
                                        e.preventDefault();
                                        focusCell(r, colIdx + 1);
                                      }
                                    }
                                  }}
                                />
                              </td>
                            );
                            if (col.key === 'totalPriceGBP') {
                              return [(
                                <td key="pricePerTicket" className="border-r border-b border-slate-200 dark:border-slate-800 px-2 py-1.5 text-right whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                  {formatMoney(computePricePerTicket(order))}
                                </td>
                              ), td, (
                                <td
                                  key="totalGbp"
                                  title={
                                    ((order as any).currency && (order as any).currency !== 'GBP')
                                      ? `${(order as any).currency} → GBP @ ${rateFor(order).toFixed(2)}`
                                      : 'GBP — no conversion'
                                  }
                                  className="border-r border-b border-slate-200 dark:border-slate-800 px-2 py-1.5 text-right whitespace-nowrap text-sm font-medium text-emerald-700 dark:text-emerald-300 cursor-help"
                                >
                                  {formatMoneyCur(computeTotalGBP(order), 'GBP')}
                                </td>
                              )];
                            }
                            if (col.key === 'priceBought') {
                              return [td, (
                                <td
                                  key="profit"
                                  className={
                                    'border-r border-b border-slate-200 dark:border-slate-800 px-2 py-1.5 text-right font-semibold whitespace-nowrap text-sm ' +
                                    (!showProfit
                                      ? 'text-gray-400'
                                      : profit >= 0
                                      ? 'text-emerald-600 dark:text-emerald-400'
                                      : 'text-red-500 dark:text-red-400')
                                  }
                                >
                                  {formatProfitCell(order)}
                                </td>
                              )];
                            }
                            if (col.key === 'helloReference') {
                              const wa = String((order as any).waStatus ?? '');
                              const dl = String((order as any).deliveryDetail ?? '');
                              const dwarn = !!(order as any).deliveryWarning;
                              const rs = (order as any).resold;
                              const rl = !!(order as any).resaleListed;
                              return [td, (
                                <td key="waStatus" className="border-r border-b border-slate-200 dark:border-slate-800 px-2 py-1.5 whitespace-nowrap">
                                  {wa && (
                                    <span className={'inline-flex px-2 py-0.5 rounded-full text-xs font-medium ' + waChipClass(wa)}>
                                      {wa}
                                    </span>
                                  )}
                                </td>
                              ), (
                                <td key="delivery" className="border-r border-b border-slate-200 dark:border-slate-800 px-2 py-1.5 whitespace-nowrap">
                                  {(() => {
                                    // Hello's own order status (pending/confirmed), shown until the
                                    // order is delivered — after which the Delivery chip says it all.
                                    const hs = String((order as any).helloStatus ?? '').toLowerCase();
                                    if (!/hello/i.test(String((order as any).platform ?? '')) || !hs || hs === 'delivered' || hs === 'rejected') return null;
                                    const cls = hs === 'confirmed'
                                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                                      : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300';
                                    return (
                                      <span className={'mr-1 inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ' + cls} title={`HelloTickets status: ${hs}`}>
                                        {hs}
                                      </span>
                                    );
                                  })()}
                                  {rs && (
                                    <span
                                      title={
                                        `Was ${rs.prevBuyer?.name || '?'} ${rs.prevBuyer?.phone || ''} → now ` +
                                        `${rs.newBuyer?.name || '?'} ${rs.newBuyer?.phone || ''}` +
                                        (rs.partial ? ' — PARTIAL: original buyer keeps the rest' : '') +
                                        (rs.newLiveOrderId ? ` (Live order ${rs.newLiveOrderId})` : '')
                                      }
                                      className="mr-1 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold cursor-help bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"
                                    >
                                      {rs.partial ? 'Part-resold' : 'Resold'}
                                    </span>
                                  )}
                                  {!rs && rl && (
                                    <span
                                      title="Relisted for resale on Live — no new buyer yet"
                                      className="mr-1 inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-help bg-purple-50 dark:bg-purple-900/20 text-purple-500 dark:text-purple-400"
                                    >
                                      For resale
                                    </span>
                                  )}
                                  {dl && (
                                    <span
                                      onClick={
                                        dl === 'Processed' || dl === 'Delivered (manual)'
                                          ? () => toggleManualDelivered(order)
                                          : undefined
                                      }
                                      title={
                                        dl === 'Processed'
                                          ? 'Click to mark delivered (manual, no proof)'
                                          : dl === 'Delivered (manual)'
                                          ? 'Marked by hand — click to undo'
                                          : undefined
                                      }
                                      className={
                                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ' +
                                        deliveryChipClass(dl) +
                                        (dl === 'Processed' || dl === 'Delivered (manual)'
                                          ? ' cursor-pointer hover:ring-1 hover:ring-slate-400'
                                          : '')
                                      }
                                    >
                                      {dl}
                                      {dwarn && (
                                        <IonIcon
                                          icon={warningOutline}
                                          className="text-amber-600 dark:text-amber-400 text-sm shrink-0"
                                          title="Tickets were sent but the buyer's phone has not acknowledged them — not delivered or seen"
                                        />
                                      )}
                                    </span>
                                  )}
                                </td>
                              )];
                            }
                            return [td];
                          })}
                          <td className="border-b border-slate-200 dark:border-slate-800 px-1 text-center whitespace-nowrap">
                            <IonButton
                              fill="clear"
                              color="medium"
                              size="small"
                              title="Copy for supplier — order number, buyer name, buyer phone (one per line)"
                              onClick={() => copyForSupplier(order)}
                            >
                              <IonIcon slot="icon-only" icon={copyOutline} />
                            </IonButton>
                            {String((order as any).buyerPhone ?? '').trim() && (
                              <IonButton
                                fill="clear"
                                color="tertiary"
                                size="small"
                                title="Deliver tickets — opens this buyer's chat with the delivery dialog"
                                onClick={() => deliverFromOrder(order)}
                              >
                                <IonIcon slot="icon-only" icon={ticketOutline} />
                              </IonButton>
                            )}
                            {String((order as any).buyerPhone ?? '').trim() && (
                              <IonButton
                                fill="clear"
                                color="medium"
                                size="small"
                                title="Open this buyer's chat"
                                onClick={() => openChatFromOrder(order)}
                              >
                                <IonIcon slot="icon-only" icon={chatbubbleOutline} />
                              </IonButton>
                            )}
                            {/hello/i.test(String((order as any).platform ?? '')) && (() => {
                              // Buttons gate on the last-known Hello status. Unknown status leaves
                              // them enabled (the server re-checks live and refuses a wrong step),
                              // so a missing value never silently locks an order.
                              const hs = String((order as any).helloStatus ?? '').toLowerCase();
                              const canConfirm = !hs || hs === 'pending';
                              const canDeliver = !hs || hs === 'confirmed';
                              const canReject = !hs || hs === 'pending';
                              return (
                              <>
                                <IonButton
                                  fill="clear"
                                  color="success"
                                  size="small"
                                  disabled={!canConfirm}
                                  title={canConfirm ? 'Confirm this pending Hello order' : `Can't confirm — Hello status is ${hs}`}
                                  onClick={() => helloAction(order, 'confirm')}
                                >
                                  <IonIcon slot="icon-only" icon={checkmarkCircleOutline} />
                                </IonButton>
                                <IonButton
                                  fill="clear"
                                  color="primary"
                                  size="small"
                                  disabled={!canDeliver}
                                  title={canDeliver ? 'Mark this confirmed Hello order delivered (no proof)' : `Can't deliver — Hello status is ${hs}`}
                                  onClick={() => helloAction(order, 'deliver')}
                                >
                                  <IonIcon slot="icon-only" icon={paperPlaneOutline} />
                                </IonButton>
                                <IonButton
                                  fill="clear"
                                  color="warning"
                                  size="small"
                                  disabled={!canReject}
                                  title={canReject ? 'Reject this order — pending orders only' : `Can't reject — Hello status is ${hs}`}
                                  onClick={() => rejectOrder(order)}
                                >
                                  <IonIcon slot="icon-only" icon={banOutline} />
                                </IonButton>
                              </>
                              );
                            })()}
                            <IonButton
                              fill="clear"
                              color="danger"
                              size="small"
                              onClick={() => setDeleteId(id)}
                            >
                              <IonIcon slot="icon-only" icon={trashOutline} />
                            </IonButton>
                          </td>
                        </tr>
                      );
                    })}
                    {paddingBottom > 0 && (
                      <tr><td colSpan={VISIBLE_COLUMNS.length + 5} style={{ height: paddingBottom }} /></tr>
                    )}
                  </>
                )}
              </tbody>
              {sortedVisibleOrders.length > 0 && (
                <tfoot>
                  <tr className="font-semibold text-sm">
                    <td
                      colSpan={4}
                      className={footerCell + ' px-2 py-2 text-right text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide'}
                    >
                      Totals · {visibleOrders.length} {visibleOrders.length === 1 ? 'order' : 'orders'}
                      {totals.needed > 0 && (
                        <span className="ml-2 text-amber-600 dark:text-amber-400 normal-case">need {totals.needed}</span>
                      )}
                    </td>
                    {/* Total tickets sold (sum of Quantity) across the filtered rows */}
                    <td className={footerCell + ' px-2 py-2 text-right text-slate-700 dark:text-slate-200'}>
                      {totals.qty}
                    </td>
                    <td colSpan={2} className={footerCell} />
                    <td className={footerCell + ' px-2 py-2 text-right text-slate-700 dark:text-slate-200'}>
                      {formatMoney(totalPricePerTicket)}
                    </td>
                    <td className={footerCell + ' px-2 py-2 text-right text-slate-700 dark:text-slate-200'}>
                      {formatMoney(totals.total)}
                    </td>
                    <td className={footerCell + ' px-2 py-2 text-right font-semibold text-emerald-700 dark:text-emerald-300'}>
                      {formatMoneyCur(totals.gbp, 'GBP')}
                    </td>
                    <td colSpan={3} className={footerCell} />
                    <td className={footerCell + ' px-2 py-2 text-right text-slate-700 dark:text-slate-200'}>
                      {formatMoney(totals.bought)}
                    </td>
                    <td
                      className={
                        footerCell + ' px-2 py-2 text-right ' +
                        (totals.profit >= 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-500 dark:text-red-400')
                      }
                    >
                      {formatMoneyCur(totals.profit, 'GBP')}
                    </td>
                    <td colSpan={7} className={footerCell} />
                  </tr>
                </tfoot>
              )}
            </table>
            </div>
            )}
          </div>
        )}

        <IonPopover
          isOpen={!!summaryMenu}
          event={summaryMenu?.event}
          onDidDismiss={() => setSummaryMenu(null)}
          showBackdrop={false}
        >
          {summaryMenu && columnSummary && (
            <div className="p-3 text-sm text-gray-900 dark:text-gray-100" style={{ minWidth: '180px' }}>
              <div className="font-semibold mb-2 text-gray-700 dark:text-gray-200">
                {COLUMNS.find((c) => c.key === summaryMenu.key)?.label ?? (summaryMenu.key === 'pricePerTicket' ? 'Price Per Ticket' : 'Profit')}
              </div>
              <div className="flex justify-between gap-4 py-0.5">
                <span className="text-gray-500 dark:text-gray-400">Count</span>
                <span className="font-medium">{columnSummary.count.toLocaleString()}</span>
              </div>
              {columnSummary.isNumber && (
                <>
                  <div className="flex justify-between gap-4 py-0.5">
                    <span className="text-gray-500 dark:text-gray-400">Sum</span>
                    <span className="font-medium">{formatMoney(columnSummary.sum)}</span>
                  </div>
                  <div className="flex justify-between gap-4 py-0.5">
                    <span className="text-gray-500 dark:text-gray-400">Average</span>
                    <span className="font-medium">{formatMoney(columnSummary.avg)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between gap-4 py-0.5 border-t border-gray-200 dark:border-gray-700 mt-1 pt-1 text-xs text-gray-400">
                <span>{sortedVisibleOrders.length.toLocaleString()} rows visible</span>
              </div>
            </div>
          )}
        </IonPopover>

        <IonPopover
          isOpen={!!filterMenu}
          event={filterMenu?.event}
          onDidDismiss={() => setFilterMenu(null)}
          showBackdrop={false}
        >
          {filterMenu && (
            <div className="p-2" style={{ width: '240px' }}>
              <div className="flex items-center justify-between mb-2 text-xs">
                <button
                  className="text-primary-600 dark:text-primary-400 font-medium"
                  onClick={() => selectAllFilter(filterMenu.key)}
                >
                  Select all
                </button>
                {isColumnFiltered(filterMenu.key) && (
                  <button
                    className="text-slate-500 dark:text-slate-400 font-medium"
                    title="Remove this column's filter entirely"
                    onClick={() =>
                      setFilters((prev) => {
                        const next = { ...prev };
                        delete next[filterMenu.key];
                        return next;
                      })
                    }
                  >
                    Reset column
                  </button>
                )}
                <button
                  className="text-primary-600 dark:text-primary-400 font-medium"
                  onClick={() => clearFilterValues(filterMenu.key)}
                >
                  Clear
                </button>
              </div>
              <input
                type="text"
                placeholder="Search…"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                className="w-full mb-2 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
              <div className="max-h-60 overflow-auto">
                {visibleFilterValues
                  .map((v) => (
                    <label
                      key={v}
                      className="flex items-center gap-2 py-1 text-sm cursor-pointer text-gray-900 dark:text-gray-100"
                    >
                      <input
                        type="checkbox"
                        checked={isFilterChecked(filterMenu.key, v)}
                        onChange={() => toggleFilterValue(filterMenu.key, v)}
                      />
                      <span className="truncate">{v === '' ? '(Blanks)' : v}</span>
                    </label>
                  ))}
                {visibleFilterValues.length === 0 && (
                  <div className="text-xs text-gray-400 py-2">No values</div>
                )}
              </div>
            </div>
          )}
        </IonPopover>

        {/* Mobile filter sheet — column list, then the same searchable value checkboxes the desktop
            header popovers use (identical state and handlers, so they compose with the chips). */}
        {filterSheetOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-end"
            onClick={() => setFilterSheetOpen(false)}
          >
            <div
              className="w-full max-h-[85vh] flex flex-col rounded-t-2xl bg-white dark:bg-gray-900 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="shrink-0 px-4 pt-3 pb-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                {filterSheetKey ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setFilterSheetKey(null)}
                      className="text-sm text-blue-600 dark:text-blue-400"
                    >
                      ‹ Back
                    </button>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {filterSheetKey === 'profit'
                        ? 'Profit'
                        : COLUMNS.find((c) => c.key === filterSheetKey)?.label || filterSheetKey}
                    </span>
                  </>
                ) : (
                  <span className="font-semibold text-gray-900 dark:text-gray-100">Filter by column</span>
                )}
                <button
                  type="button"
                  onClick={() => setFilterSheetOpen(false)}
                  className="ml-auto px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium"
                >
                  Done
                </button>
              </div>

              {!filterSheetKey ? (
                <>
                  <div className="flex-1 overflow-y-auto">
                    {FILTER_KEYS.map((key) => {
                      const label =
                        key === 'profit' ? 'Profit' : COLUMNS.find((c) => c.key === key)?.label || key;
                      const active = isColumnFiltered(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            setFilterSearch('');
                            setFilterSheetKey(key);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm border-b border-slate-100 dark:border-slate-800 active:bg-blue-50 dark:active:bg-blue-950/30"
                        >
                          <span className={active ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-800 dark:text-gray-200'}>
                            {label}
                          </span>
                          {active && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                              {filters[key].size} selected
                            </span>
                          )}
                          <IonIcon icon={active ? funnel : funnelOutline} className={'ml-auto ' + (active ? 'text-blue-500' : 'text-slate-300 dark:text-slate-600')} />
                        </button>
                      );
                    })}
                  </div>
                  {activeFilterCount > 0 && (
                    <div className="shrink-0 px-4 py-3 border-t border-slate-200 dark:border-slate-700" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
                      <button
                        type="button"
                        onClick={clearAllFilters}
                        className="w-full py-2 rounded-lg border border-red-300 dark:border-red-800 text-sm text-red-600 dark:text-red-400"
                      >
                        Clear all filters ({activeFilterCount})
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="shrink-0 px-4 py-2 space-y-2 border-b border-slate-100 dark:border-slate-800">
                    <input
                      type="text"
                      placeholder="Search values…"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100"
                    />
                    <div className="flex items-center justify-between text-sm">
                      <button
                        className="text-blue-600 dark:text-blue-400 font-medium"
                        onClick={() => selectAllFilter(filterSheetKey)}
                      >
                        Select all
                      </button>
                      <button
                        className="text-blue-600 dark:text-blue-400 font-medium"
                        onClick={() => clearFilterValues(filterSheetKey)}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4 py-1" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                    {visibleFilterValues.map((v) => (
                      <label
                        key={v}
                        className="flex items-center gap-3 py-2.5 text-sm border-b border-slate-100 dark:border-slate-800 text-gray-900 dark:text-gray-100"
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={isFilterChecked(filterSheetKey, v)}
                          onChange={() => toggleFilterValue(filterSheetKey, v)}
                        />
                        <span className="truncate">{v === '' ? '(Blanks)' : v}</span>
                      </label>
                    ))}
                    {visibleFilterValues.length === 0 && (
                      <div className="text-xs text-gray-400 py-3">No values</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Mobile edit sheet — every column as a labelled field; saves only what changed */}
        {sheetOrder && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-end"
            onClick={() => setSheetOrderId(null)}
          >
            <div
              className="w-full max-h-[88vh] flex flex-col rounded-t-2xl bg-white dark:bg-gray-900 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="shrink-0 px-4 pt-3 pb-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    Order {String(sheetOrder.orderId ?? '') || '(no id)'}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 flex gap-3">
                    <span className="text-emerald-700 dark:text-emerald-300 font-medium">
                      {formatMoneyCur(computeTotalGBP(sheetOrder), 'GBP')}
                    </span>
                    {hasPriceBought(sheetOrder) && <span>Profit {formatProfitCell(sheetOrder)}</span>}
                    {sourcingStatus(sheetOrder) === 'partial' && (
                      <span className="text-amber-600 dark:text-amber-400 font-semibold">
                        Need {ticketsOutstanding(sheetOrder)} more ticket
                        {ticketsOutstanding(sheetOrder) === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSheetOrderId(null);
                    setDeleteId(rowId(sheetOrder));
                  }}
                  className="ml-auto shrink-0 p-2 text-red-500"
                  title="Delete order"
                >
                  <IonIcon icon={trashOutline} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
                {COLUMNS.map((col) => {
                  const key = col.key as string;
                  const wide = ['eventName', 'remarks', 'agesNote', 'notes', 'buyerEmail', 'buyerName'].includes(key);
                  return (
                    <label key={key} className={'block text-xs ' + (wide ? 'col-span-2' : '')}>
                      <span className="text-slate-500 dark:text-slate-400">{col.label}</span>
                      <input
                        type={col.type === 'date' ? 'date' : 'text'}
                        inputMode={col.type === 'number' ? 'decimal' : undefined}
                        value={sheetValues[key] ?? ''}
                        onChange={(e) =>
                          setSheetValues((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                        className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100"
                      />
                    </label>
                  );
                })}
              </div>
              <div className="shrink-0 px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex gap-2" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
                {String((sheetOrder as any).buyerPhone ?? '').trim() && (
                  <>
                    <a
                      href={`tel:${String((sheetOrder as any).buyerPhone).trim()}`}
                      className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-200"
                    >
                      Call buyer
                    </a>
                    <button
                      type="button"
                      onClick={() => deliverFromOrder(sheetOrder)}
                      className="px-3 py-2 rounded-lg border border-teal-400 text-sm text-teal-600 dark:text-teal-300"
                    >
                      Deliver tickets
                    </button>
                    <button
                      type="button"
                      onClick={() => openChatFromOrder(sheetOrder)}
                      className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-200"
                    >
                      Chat
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setSheetOrderId(null)}
                  className="ml-auto px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveSheet}
                  disabled={saving}
                  className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rejected-orders record: read-only, kept out of the table */}
        {rejectedList !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setRejectedList(null)}>
            <div
              className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-3xl w-[92%] max-h-[80vh] overflow-y-auto p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Rejected orders ({rejectedList.length})
                </h2>
                <button className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" onClick={() => setRejectedList(null)}>
                  Close
                </button>
              </div>
              {rejectedList.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Nothing rejected yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-1.5 pr-2">Order</th>
                      <th className="py-1.5 pr-2">Event</th>
                      <th className="py-1.5 pr-2">Buyer</th>
                      <th className="py-1.5 pr-2 text-right">Qty</th>
                      <th className="py-1.5 pr-2 text-right">Total</th>
                      <th className="py-1.5">Rejected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejectedList.map((r: any) => (
                      <tr key={String(r._id)} className="border-b border-slate-100 dark:border-slate-800 text-gray-800 dark:text-gray-200">
                        <td className="py-1.5 pr-2 whitespace-nowrap">{r.orderId}</td>
                        <td className="py-1.5 pr-2">{r.eventName}</td>
                        <td className="py-1.5 pr-2 whitespace-nowrap">{r.buyerName}</td>
                        <td className="py-1.5 pr-2 text-right">{r.quantity}</td>
                        <td className="py-1.5 pr-2 text-right whitespace-nowrap">{Number(r.totalPriceGBP ?? 0).toFixed(2)} {r.currency}</td>
                        <td className="py-1.5 whitespace-nowrap text-slate-500">{r.rejectedAt ? new Date(r.rejectedAt).toLocaleString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        <IonModal isOpen={privatePick} onDidDismiss={() => { setPrivatePick(false); setPrivatePickQuery(''); }}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Private sale — which match?</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setPrivatePick(false)}>Cancel</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div className="p-3 flex flex-col gap-2">
              <input
                autoFocus
                type="text"
                value={privatePickQuery}
                onChange={(e) => setPrivatePickQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && privatePickMatches.length === 1) pickPrivateMatch(privatePickMatches[0]); }}
                placeholder="Search matches with orders…"
                className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-900 text-sm"
              />
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                {privatePickMatches.length} match{privatePickMatches.length === 1 ? '' : 'es'} with orders on file (upcoming first). Pick one to create the sale with that name and date.
              </div>
              <div className="flex flex-col divide-y divide-slate-200 dark:divide-slate-700 rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
                {privatePickMatches.slice(0, 200).map((m) => (
                  <button
                    key={m.name + '||' + m.date}
                    type="button"
                    onClick={() => pickPrivateMatch(m)}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <span className="truncate">{m.name}</span>
                    <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">
                      {m.date ? shortDate(new Date(m.date + 'T12:00:00').getTime()) : 'no date'} · {m.n} order{m.n === 1 ? '' : 's'}
                    </span>
                  </button>
                ))}
                {privatePickMatches.length === 0 && (
                  <div className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">No match on file with that name.</div>
                )}
              </div>
              {privatePickQuery.trim() && (
                <button
                  type="button"
                  onClick={() => pickPrivateMatch(null)}
                  className="self-start text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Use “{privatePickQuery.trim()}” as a new match name instead
                </button>
              )}
            </div>
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={!!deleteId}
          onDidDismiss={() => setDeleteId(null)}
          header="Delete order"
          message="Are you sure you want to delete this order? This cannot be undone."
          buttons={[
            { text: 'Cancel', role: 'cancel' },
            { text: 'Delete', role: 'destructive', handler: confirmDelete },
          ]}
        />

        <IonToast
          isOpen={!!toast}
          message={toast?.message}
          color={toast?.color}
          duration={3000}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default Orders;
